import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  Paperclip,
  X,
  ChevronLeft,
  ChevronDown,
  Settings,
  File as FileIcon,
  Image as ImageIcon,
  Mic,
  Minus,
  Search,
  Square,
  CheckCircle2,
  LoaderCircle,
} from 'lucide-react';
import {
  assignConversationToAttendant,
  listInternalChatAttendants,
  listInternalChatThreads,
  createInternalChatThread,
  getInternalChatThreadMessages,
  markInternalChatThreadRead,
  reportInternalChatClientError,
  sendInternalChatThreadMessage,
  updateAttendantMe,
} from '../api/api';
import { useChatContext } from '../context/useChatContext.js';
import Avatar from '../components/Avatar';
import AudioPlayer from '../components/AudioPlayer';
import ImageLightbox from '../components/common/ImageLightbox.jsx';
import { resolveMediaUrl } from '../utils/media';
import { APP_TIME_ZONE } from '../config.js';
import {
  INTERNAL_CHAT_EVENT,
  dispatchChatStateRefreshEvent,
} from '../utils/internalChatEvents.js';
import { showError, showSuccess } from '../utils/toast';
import { extractApiErrorMessage } from '../utils/apiErrors';
import {
  INTERNAL_CHAT_UNREAD_UPDATED_EVENT,
  clearInternalChatUnreadThreads,
  getInternalChatUnreadMapKey,
  incrementInternalChatUnreadThread,
  readInternalChatUnreadMap,
  replaceInternalChatUnreadMap,
} from '../utils/internalChatUnread.js';
import { validateFileSize } from '../components/chat/utils/chatWindowUtils';
import {
  INTERNAL_CHAT_LAUNCHER_POSITIONS,
  normalizeInternalChatLauncherPosition,
} from '../utils/internalChatLauncher.js';
import { parseTimestampToDate } from '../utils/datetime.js';

import './internal-chat.css';

const DEFAULT_HISTORY_LIMIT = 80;
const THREADS_PAGE_SIZE = 100;
const ATTENDANTS_DIRECTORY_PAGE_SIZE = 100;
const SIDEBAR_RENDER_INITIAL = 60;
const SIDEBAR_RENDER_BATCH = 60;
const SIDEBAR_SCROLL_THRESHOLD_PX = 120;
const INTERNAL_CHAT_DEBUG_FLAG = '__BLUEZAP_INTERNAL_CHAT_DEBUG__';
const INTERNAL_TRANSFER_MESSAGE_TYPE = 'conversation_transfer_request';
const INTERNAL_TRANSFER_ACTION_TYPES = new Set([
  'conversation_transfer_request',
  'transfer_attendance',
]);
const internalChatThreadsRequestCache = new Map();
const PHOTO_CANDIDATE_KEYS = [
  'photo',
  'photograph',
  'avatar',
  'picture',
  'photo_url',
  'photoUrl',
  'photograph_url',
  'avatar_url',
  'avatarUrl',
  'profile_photo',
  'profilePhoto',
  'profile_picture',
  'profilePicture',
  'image',
  'image_url',
  'imageUrl',
];

const resolveAttendantPhoto = (participant) => {
  if (!participant || typeof participant !== 'object') return '';
  for (const key of PHOTO_CANDIDATE_KEYS) {
    const value = participant[key];
    if (value) return value;
  }
  const nestedSources = [participant.user, participant.profile];
  for (const source of nestedSources) {
    if (!source || typeof source !== 'object') continue;
    for (const key of PHOTO_CANDIDATE_KEYS) {
      const value = source[key];
      if (value) return value;
    }
  }
  return '';
};

const resolveAttendantDisplayName = (attendant) => {
  if (!attendant || typeof attendant !== 'object') return 'Atendente';
  const rawName = typeof attendant.name === 'string' ? attendant.name.trim() : '';
  if (rawName) return rawName;
  if (attendant.id) return `Atendente #${attendant.id}`;
  return 'Atendente';
};

const normalizeInternalChatSearch = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const internalChatAttendantMatchesSearch = (attendant, normalizedSearchTerm) => {
  if (!normalizedSearchTerm) return true;
  const fields = [
    attendant?.name,
    attendant?.displayName,
    attendant?.email,
    attendant?.id,
    attendant?.user?.name,
    attendant?.user?.email,
    attendant?.profile?.name,
    attendant?.profile?.email,
  ];
  return fields.some((field) => normalizeInternalChatSearch(field).includes(normalizedSearchTerm));
};

const normalizeComparableId = (value) => {
  if (value === null || value === undefined) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const parsed = Number(trimmed);
  if (Number.isFinite(parsed)) return `number:${parsed}`;
  return `string:${trimmed}`;
};

const isSameId = (a, b) => {
  const normalizedA = normalizeComparableId(a);
  const normalizedB = normalizeComparableId(b);
  return Boolean(normalizedA && normalizedB && normalizedA === normalizedB);
};

const resolveParticipantPresence = (presenceMap, participantId) => {
  const normalizedParticipantId = normalizeComparableId(participantId);
  if (!normalizedParticipantId || !presenceMap || typeof presenceMap !== 'object') {
    return undefined;
  }

  for (const [presenceId, isOnline] of Object.entries(presenceMap)) {
    if (normalizeComparableId(presenceId) === normalizedParticipantId) {
      return Boolean(isOnline);
    }
  }

  return undefined;
};

const parsePositiveId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getNestedValue = (source, path) => {
  let current = source;
  for (const key of path) {
    if (!current || typeof current !== 'object') return undefined;
    current = current[key];
  }
  return current;
};

const getFirstPositiveId = (source, paths) => {
  for (const path of paths) {
    const parsed = parsePositiveId(getNestedValue(source, path));
    if (parsed !== null) return parsed;
  }
  return null;
};

const resolveTransferRequestAction = (message, currentAttendantId) => {
  const metadata = message?.metadata && typeof message.metadata === 'object'
    ? message.metadata
    : {};
  const payload = metadata.payload && typeof metadata.payload === 'object'
    ? metadata.payload
    : {};
  const messageType = String(
    message?.message_type ||
      message?.messageType ||
      metadata.message_type ||
      metadata.type ||
      ''
  ).trim().toLowerCase();
  const actionType = String(
    metadata.action_type ||
      metadata.action ||
      payload.action_type ||
      payload.action ||
      ''
  ).trim().toLowerCase();
  if (
    messageType !== INTERNAL_TRANSFER_MESSAGE_TYPE &&
    !INTERNAL_TRANSFER_ACTION_TYPES.has(actionType)
  ) {
    return null;
  }

  const conversationId = getFirstPositiveId(metadata, [
    ['conversation_id'],
    ['attendance_id'],
    ['payload', 'conversation_id'],
    ['payload', 'attendance_id'],
    ['related_entity', 'id'],
    ['entity', 'id'],
  ]);
  const requesterAttendantId = getFirstPositiveId(metadata, [
    ['requester_attendant_id'],
    ['from_attendant_id'],
    ['payload', 'requester_attendant_id'],
    ['payload', 'from_attendant_id'],
  ]);
  const requestedFromAttendantId = getFirstPositiveId(metadata, [
    ['requested_from_attendant_id'],
    ['to_attendant_id'],
    ['payload', 'requested_from_attendant_id'],
    ['payload', 'to_attendant_id'],
  ]);

  if (!conversationId || !requesterAttendantId) return null;
  if (requestedFromAttendantId && currentAttendantId && requestedFromAttendantId !== currentAttendantId) {
    return null;
  }
  if (requesterAttendantId === currentAttendantId) return null;

  const status = String(metadata.status || payload.status || 'pending').trim().toLowerCase();
  return {
    conversationId,
    requesterAttendantId,
    requesterName:
      metadata.requester_attendant_name ||
      payload.requester_attendant_name ||
      message?.sender_name ||
      `Atendente #${requesterAttendantId}`,
    status,
  };
};

const formatTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: APP_TIME_ZONE,
  });
};

const resolveTimestampMs = (value) => {
  const date = parseTimestampToDate(value);
  return date ? date.getTime() : null;
};

const safeTimestamp = (value, fallback = 0) => {
  const parsed = resolveTimestampMs(value);
  return parsed === null ? fallback : parsed;
};

const resolveThreadActivityMs = (thread, fallback = 0) => {
  const activityTimestamp = thread?.last_message_at ?? thread?.created_at ?? null;
  const parsed = resolveTimestampMs(activityTimestamp);
  return parsed === null ? fallback : parsed;
};

const compareByActivityDesc = (a, b) => {
  const timestampA = resolveThreadActivityMs(a, 0);
  const timestampB = resolveThreadActivityMs(b, 0);
  if (timestampA !== timestampB) return timestampB - timestampA;
  return Number(b?.id || 0) - Number(a?.id || 0);
};

const compareMessagesAsc = (a, b) => {
  const timestampA = safeTimestamp(a?.created_at ?? a?.createdAt ?? a?.timestamp, 0);
  const timestampB = safeTimestamp(b?.created_at ?? b?.createdAt ?? b?.timestamp, 0);
  if (timestampA !== timestampB) return timestampA - timestampB;
  return Number(a?.id || 0) - Number(b?.id || 0);
};

const dedupeAndSortMessages = (items) => {
  const byKey = new Map();
  let fallbackIndex = 0;
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item) return;
    const key =
      item.id !== null && item.id !== undefined
        ? `id:${normalizeComparableId(item.id)}`
        : `fallback:${fallbackIndex++}`;
    byKey.set(key, item);
  });
  return Array.from(byKey.values()).sort(compareMessagesAsc);
};

const resolveInternalMessageThreadId = (message, payload = null) => (
  message?.thread_id ??
  message?.threadId ??
  payload?.thread_id ??
  payload?.threadId ??
  payload?.thread?.id ??
  null
);

const isImageFile = (fileName = '') => {
  const lowered = fileName.toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'].some((ext) => lowered.endsWith(ext));
};

const isVideoFile = (fileName = '') => {
  const lowered = fileName.toLowerCase();
  return ['.mp4', '.webm', '.mov', '.mkv', '.avi'].some((ext) => lowered.endsWith(ext));
};

const isAudioFile = (fileName = '') => {
  const lowered = fileName.toLowerCase();
  if (/(audio|voice|ptt)/.test(lowered)) return true;
  return ['.mp3', '.ogg', '.oga', '.wav', '.m4a', '.aac', '.opus'].some((ext) => lowered.endsWith(ext));
};

const resolveMediaKind = (message, fileName = '', mediaUrl = '') => {
  const rawType = String(
    message?.type || message?.media_type || message?.mediaType || ''
  ).toLowerCase();
  if (rawType.includes('image')) return 'image';
  if (rawType.includes('audio') || rawType.includes('ptt')) return 'audio';
  if (rawType.includes('video')) return 'video';
  if (isImageFile(fileName || mediaUrl)) return 'image';
  if (isAudioFile(fileName || mediaUrl)) return 'audio';
  if (isVideoFile(fileName || mediaUrl)) return 'video';
  return null;
};

const resolveMediaLabel = (message, fileName = '', mediaUrl = '') => {
  const kind = resolveMediaKind(message, fileName, mediaUrl);
  if (kind === 'image') return 'Imagem';
  if (kind === 'audio') return 'Áudio';
  if (kind === 'video') return 'Vídeo';
  if (fileName || mediaUrl) return 'Arquivo';
  return '';
};

const getAudioExtension = (mimeType = '') => {
  const normalized = mimeType.toLowerCase();
  if (normalized.includes('ogg')) return 'ogg';
  if (normalized.includes('webm')) return 'webm';
  if (normalized.includes('wav')) return 'wav';
  if (normalized.includes('mpeg') || normalized.includes('mp3')) return 'mp3';
  if (normalized.includes('m4a')) return 'm4a';
  if (normalized.includes('aac')) return 'aac';
  if (normalized.includes('opus')) return 'opus';
  return 'webm';
};

const isInternalChatDebugEnabled = () =>
  typeof window !== 'undefined' && window[INTERNAL_CHAT_DEBUG_FLAG] === true;

const buildThreadsRequestCacheKey = (options = {}) => {
  const participantAttendantId = Number(options?.participantAttendantId);
  const limit = Number(options?.limit);
  const offset = Number(options?.offset);
  return JSON.stringify({
    participantAttendantId:
      Number.isFinite(participantAttendantId) && participantAttendantId > 0
        ? Math.trunc(participantAttendantId)
        : null,
    limit: Number.isFinite(limit) && limit > 0 ? Math.trunc(limit) : null,
    offset: Number.isFinite(offset) && offset >= 0 ? Math.trunc(offset) : 0,
  });
};

const listInternalChatThreadsDeduped = (options = {}) => {
  const cacheKey = buildThreadsRequestCacheKey(options);
  const cachedPromise = internalChatThreadsRequestCache.get(cacheKey);
  if (cachedPromise) {
    return cachedPromise;
  }

  const requestPromise = listInternalChatThreads(options).finally(() => {
    if (internalChatThreadsRequestCache.get(cacheKey) === requestPromise) {
      internalChatThreadsRequestCache.delete(cacheKey);
    }
  });

  internalChatThreadsRequestCache.set(cacheKey, requestPromise);
  return requestPromise;
};

const logInternalChatPerf = (label, startedAt, extra = undefined) => {
  if (!isInternalChatDebugEnabled() || typeof performance === 'undefined') return;
  const duration = Math.round((performance.now() - startedAt) * 100) / 100;
  if (extra && typeof extra === 'object') {
    console.log(`[InternalChat] ${label}: ${duration}ms`, extra);
    return;
  }
  console.log(`[InternalChat] ${label}: ${duration}ms`);
};

function DeferredAudioPlayer({ url, rootRef }) {
  const containerRef = useRef(null);
  const [shouldRenderPlayer, setShouldRenderPlayer] = useState(false);

  useEffect(() => {
    setShouldRenderPlayer(false);
  }, [url]);

  useEffect(() => {
    if (shouldRenderPlayer) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setShouldRenderPlayer(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRenderPlayer(true);
          observer.disconnect();
        }
      },
      {
        root: rootRef?.current || null,
        rootMargin: '220px 0px',
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [rootRef, shouldRenderPlayer, url]);

  return (
    <div ref={containerRef} className="internal-chat-media-audio">
      {shouldRenderPlayer ? (
        <AudioPlayer url={url} />
      ) : (
        <button
          type="button"
          className="internal-chat-media-audio__placeholder"
          onClick={() => setShouldRenderPlayer(true)}
        >
          Carregar áudio
        </button>
      )}
    </div>
  );
}

export function InternalChatView({
  variant = 'page',
  focusRequest = null,
  onClose = null,
  onMinimize = null,
  onUnreadCountChange = null,
} = {}) {
  const {
    internalChatPresence,
    attendantId: ctxAttendantId,
    companyId: ctxCompanyId,
    attendantData,
    isAdmin,
    canAccessInternalChat,
  } = useChatContext();
  const [privateUnreadMap, setPrivateUnreadMap] = useState({});
  const [threads, setThreads] = useState([]);
  const [threadsOffset, setThreadsOffset] = useState(0);
  const [threadsHasMore, setThreadsHasMore] = useState(false);
  const [selectedAttendantId, setSelectedAttendantId] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [messagesHasOlder, setMessagesHasOlder] = useState(false);
  const [messagesOldestId, setMessagesOldestId] = useState(null);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [threadsError, setThreadsError] = useState(null);
  const [messagesError, setMessagesError] = useState(null);
  const [transferActionByMessageId, setTransferActionByMessageId] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [compactView, setCompactView] = useState('list');
  const [selectedFile, setSelectedFile] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  // O chat pessoal é o padrão; o monitoramento é escolhido nesta tela.
  const [adminViewMode, setAdminViewMode] = useState('mine');
  const [selectedAdminAttendantId, setSelectedAdminAttendantId] = useState(null);
  const [isSidebarSettingsOpen, setIsSidebarSettingsOpen] = useState(false);
  const [directoryAttendants, setDirectoryAttendants] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState(null);
  const [directorySearchInput, setDirectorySearchInput] = useState('');
  const [directorySearchTerm, setDirectorySearchTerm] = useState('');
  const [directoryOffset, setDirectoryOffset] = useState(0);
  const [directoryHasMore, setDirectoryHasMore] = useState(false);
  const [adminDirectoryAttendants, setAdminDirectoryAttendants] = useState([]);
  const [adminDirectoryLoading, setAdminDirectoryLoading] = useState(false);
  const [adminDirectoryError, setAdminDirectoryError] = useState(null);
  const [adminDirectorySearchInput, setAdminDirectorySearchInput] = useState('');
  const [adminDirectorySearchTerm, setAdminDirectorySearchTerm] = useState('');
  const [adminDirectoryOffset, setAdminDirectoryOffset] = useState(0);
  const [adminDirectoryHasMore, setAdminDirectoryHasMore] = useState(false);
  const [selectedAdminAttendantName, setSelectedAdminAttendantName] = useState('');
  const [sidebarRenderLimit, setSidebarRenderLimit] = useState(SIDEBAR_RENDER_INITIAL);
  const bottomRef = useRef(null);
  const messageListRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingMimeTypeRef = useRef('');
  const dragDepthRef = useRef(0);
  const threadsRequestSeqRef = useRef(0);
  const messagesRequestSeqRef = useRef(0);
  const directoryRequestSeqRef = useRef(0);
  const adminDirectoryRequestSeqRef = useRef(0);
  const selectedThreadIdRef = useRef(null);
  const messagesRef = useRef([]);
  const threadsRef = useRef([]);
  const sidebarListRef = useRef(null);
  const initialLoadStartRef = useRef(null);
  const readyMarkLoggedRef = useRef(false);
  const pendingScrollBehaviorRef = useRef(null);
  const pendingOlderMessagesScrollRef = useRef(null);
  const threadsLoadTriggerRef = useRef(null);
  const threadSelectionStartRef = useRef(null);
  const processedFocusRequestNonceRef = useRef(null);
  const processedRouteTargetKeyRef = useRef(null);
  const transferActionStatusRef = useRef(new Map());
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [searchParams] = useSearchParams();
  const isFloating = variant === 'floating';
  const [pendingLauncherPosition, setPendingLauncherPosition] = useState(null);
  const [isSavingLauncherPosition, setIsSavingLauncherPosition] = useState(false);
  const [shortcutMenuOpen, setShortcutMenuOpen] = useState(false);
  const [imageViewer, setImageViewer] = useState(null);
  const shortcutMenuRef = useRef(null);
  const settingsModalHost = useMemo(() => {
    if (typeof document === 'undefined') return null;
    return document.getElementById('modal-root') || document.body;
  }, []);

  const attendantId = useMemo(() => (ctxAttendantId ? Number(ctxAttendantId) : null), [ctxAttendantId]);
  const companyId = useMemo(() => (ctxCompanyId ? Number(ctxCompanyId) : null), [ctxCompanyId]);
  const missingAuth = !attendantId || !companyId || !canAccessInternalChat;
  const persistedLauncherPosition = useMemo(
    () =>
      normalizeInternalChatLauncherPosition(
        attendantData?.internal_chat_launcher_position
      ),
    [attendantData?.internal_chat_launcher_position]
  );
  const launcherPosition = pendingLauncherPosition ?? persistedLauncherPosition;

  useEffect(() => {
    initialLoadStartRef.current =
      typeof performance !== 'undefined' ? performance.now() : null;
    readyMarkLoggedRef.current = false;
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  const handleChangeLauncherPosition = useCallback(
    async (position) => {
      const nextPosition = normalizeInternalChatLauncherPosition(position);
      if (isSavingLauncherPosition) return;
      if (nextPosition === launcherPosition) {
        setShortcutMenuOpen(false);
        return;
      }
      if (!attendantData?.email) {
        setError('Não foi possível identificar o perfil do atendente para salvar o atalho.');
        setShortcutMenuOpen(false);
        return;
      }

      setError(null);
      setPendingLauncherPosition(nextPosition);
      setIsSavingLauncherPosition(true);
      setShortcutMenuOpen(false);

      try {
        await updateAttendantMe({
          email: attendantData.email,
          internal_chat_launcher_position: nextPosition,
        });
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('bluezap:attendant-updated', {
              detail: { attendantId },
            })
          );
        }
      } catch (err) {
        setPendingLauncherPosition(null);
        setError(err?.message || 'Não foi possível salvar o atalho do chat interno.');
      } finally {
        setIsSavingLauncherPosition(false);
      }
    },
    [attendantData?.email, attendantId, isSavingLauncherPosition, launcherPosition]
  );

  const syncUnreadMapFromStorage = useCallback(() => {
    if (!companyId || !attendantId) {
      setPrivateUnreadMap({});
      return;
    }
    setPrivateUnreadMap(readInternalChatUnreadMap(companyId, attendantId));
  }, [attendantId, companyId]);

  const markThreadReadOnServer = useCallback(
    async (threadId, throughMessageId = null) => {
      if (missingAuth || !threadId) return null;
      try {
        const summary = await markInternalChatThreadRead(threadId, throughMessageId);
        if (summary?.unread_map && typeof summary.unread_map === 'object') {
          replaceInternalChatUnreadMap(
            companyId,
            attendantId,
            summary.unread_map,
            { source: 'internal-chat-view:server-read' }
          );
        }
        return summary;
      } catch (err) {
        console.error('Falha ao registrar leitura da conversa interna', {
          threadId,
          status: err?.status ?? null,
        });
        return null;
      }
    },
    [attendantId, companyId, missingAuth]
  );

  useEffect(() => {
    syncUnreadMapFromStorage();
  }, [syncUnreadMapFromStorage]);

  useEffect(() => {
    if (!companyId || !attendantId || typeof window === 'undefined') return undefined;

    const handleUnreadUpdated = (event) => {
      const targetCompanyId = Number(event?.detail?.companyId ?? companyId);
      if (Number.isFinite(targetCompanyId) && targetCompanyId !== Number(companyId)) {
        return;
      }
      const targetAttendantId = Number(event?.detail?.attendantId ?? attendantId);
      if (Number.isFinite(targetAttendantId) && targetAttendantId !== attendantId) {
        return;
      }

      if (event?.detail?.unreadMap && typeof event.detail.unreadMap === 'object') {
        setPrivateUnreadMap(event.detail.unreadMap);
        return;
      }

      syncUnreadMapFromStorage();
    };

    const handleStorage = (event) => {
      if (
        event?.key &&
        event.key !== getInternalChatUnreadMapKey(companyId, attendantId)
      ) {
        return;
      }
      syncUnreadMapFromStorage();
    };

    window.addEventListener(INTERNAL_CHAT_UNREAD_UPDATED_EVENT, handleUnreadUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(INTERNAL_CHAT_UNREAD_UPDATED_EVENT, handleUnreadUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [attendantId, companyId, syncUnreadMapFromStorage]);

  useEffect(() => {
    if (pendingLauncherPosition !== null && persistedLauncherPosition === pendingLauncherPosition) {
      setPendingLauncherPosition(null);
    }
  }, [pendingLauncherPosition, persistedLauncherPosition]);

  useEffect(() => {
    if (!shortcutMenuOpen || typeof document === 'undefined') return undefined;

    const handlePointerDown = (event) => {
      if (!shortcutMenuRef.current?.contains(event.target)) {
        setShortcutMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShortcutMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [shortcutMenuOpen]);

  useEffect(() => {
    if (!isSidebarSettingsOpen || typeof document === 'undefined') return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsSidebarSettingsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSidebarSettingsOpen]);

  useEffect(() => {
    const nextValue = directorySearchInput.trim();
    const timer = window.setTimeout(() => {
      setDirectorySearchTerm((prev) => (prev === nextValue ? prev : nextValue));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [directorySearchInput]);

  useEffect(() => {
    const nextValue = adminDirectorySearchInput.trim();
    const timer = window.setTimeout(() => {
      setAdminDirectorySearchTerm((prev) => (prev === nextValue ? prev : nextValue));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [adminDirectorySearchInput]);

  useEffect(() => {
    setSidebarRenderLimit(SIDEBAR_RENDER_INITIAL);
  }, [
    adminViewMode,
    selectedAdminAttendantId,
    directorySearchTerm,
    directoryAttendants.length,
    threads.length,
    isAdmin,
  ]);

  const requestedTargetId = useMemo(() => {
    const raw =
      searchParams.get('target') ||
      searchParams.get('attendant') ||
      searchParams.get('attendantId');
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }, [searchParams]);

  const requestedPrefill = useMemo(() => searchParams.get('prefill') || '', [searchParams]);
  const shouldLoadMineDirectory = !missingAuth && (!isAdmin || adminViewMode === 'mine');
  const shouldLoadAdminDirectory =
    !missingAuth && isAdmin && adminViewMode === 'all' && isSidebarSettingsOpen;
  const shouldLoadThreads =
    !missingAuth &&
    (
      (isAdmin && adminViewMode === 'all' && Number(selectedAdminAttendantId) > 0) ||
      ((!isAdmin || adminViewMode === 'mine') && Number(attendantId) > 0)
    );

  const threadByParticipant = useMemo(() => {
    const map = new Map();
    threads.forEach((thread) => {
      if (thread?.can_reply === false) return;
      map.set(thread.participant.id, thread);
    });
    return map;
  }, [threads]);

  const attendantsList = useMemo(() => {
    const map = new Map();
    const normalizedDirectorySearchTerm = normalizeInternalChatSearch(directorySearchTerm);
    const directoryMatchedAttendantIds = new Set();
    directoryAttendants.forEach((attendant) => {
      const rawPhoto = resolveAttendantPhoto(attendant);
      const displayName = resolveAttendantDisplayName(attendant);
      if (attendant?.id != null) {
        directoryMatchedAttendantIds.add(String(attendant.id));
      }
      map.set(attendant.id, {
        id: attendant.id,
        name: attendant.name,
        displayName,
        photo: rawPhoto ? resolveMediaUrl(rawPhoto) : '',
        baseIsOnline: !!attendant.is_online,
        thread: threadByParticipant.get(attendant.id) || null,
      });
    });
    threads.forEach((thread) => {
      const participant = thread.participant;
      if (!participant) return;
      const participantWasReturnedBySearch = directoryMatchedAttendantIds.has(String(participant.id));
      if (
        normalizedDirectorySearchTerm &&
        !participantWasReturnedBySearch &&
        !internalChatAttendantMatchesSearch(participant, normalizedDirectorySearchTerm)
      ) {
        return;
      }
      const rawPhoto = resolveAttendantPhoto(participant);
      const displayName = resolveAttendantDisplayName(participant);
      map.set(participant.id, {
        id: participant.id,
        name: participant.name,
        displayName,
        photo: rawPhoto ? resolveMediaUrl(rawPhoto) : '',
        thread,
      });
    });

    const list = Array.from(map.values()).map((item) => {
      return {
        ...item,
        displayName: item.displayName || resolveAttendantDisplayName(item),
        isOnline:
          resolveParticipantPresence(internalChatPresence, item.id) ??
          item.thread?.participant?.is_online ??
          item.baseIsOnline ??
          false,
        lastActivityMs: item.thread ? resolveThreadActivityMs(item.thread, null) : null,
      };
    });

    return list.sort((a, b) => {
      const aTime = a.lastActivityMs ?? Number.NEGATIVE_INFINITY;
      const bTime = b.lastActivityMs ?? Number.NEGATIVE_INFINITY;
      if (bTime !== aTime) return bTime - aTime;
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
  }, [directoryAttendants, directorySearchTerm, threadByParticipant, threads, internalChatPresence]);

  const visibleAdminThreads = useMemo(() => {
    if (!isAdmin || adminViewMode !== 'all') return [];
    return threads;
  }, [adminViewMode, isAdmin, threads]);
  const displayedAttendants = useMemo(
    () => attendantsList.slice(0, sidebarRenderLimit),
    [attendantsList, sidebarRenderLimit]
  );
  const displayedAdminThreads = useMemo(
    () => visibleAdminThreads.slice(0, sidebarRenderLimit),
    [sidebarRenderLimit, visibleAdminThreads]
  );
  const hasHiddenRenderedAttendants =
    (!isAdmin || adminViewMode === 'mine') && displayedAttendants.length < attendantsList.length;
  const hasHiddenRenderedAdminThreads =
    isAdmin && adminViewMode === 'all' && displayedAdminThreads.length < visibleAdminThreads.length;

  const scopedUnreadCount = useMemo(() => {
    const unreadMap = privateUnreadMap || {};
    if (!isAdmin) {
      return Object.values(unreadMap).reduce((acc, value) => acc + (Number(value) || 0), 0);
    }

    const scopedThreadIds = new Set(
      (threads || [])
        .filter((thread) => adminViewMode === 'all' || thread?.can_reply !== false)
        .map((thread) => String(thread.id))
    );
    if (scopedThreadIds.size === 0) return 0;

    return Object.entries(unreadMap).reduce((acc, [threadId, count]) => {
      if (!scopedThreadIds.has(String(threadId))) return acc;
      return acc + (Number(count) || 0);
    }, 0);
  }, [adminViewMode, isAdmin, privateUnreadMap, threads]);

  useEffect(() => {
    if (typeof onUnreadCountChange === 'function') {
      onUnreadCountChange(scopedUnreadCount);
    }
  }, [onUnreadCountChange, scopedUnreadCount]);

  const activeThread = useMemo(
    () => threads.find((thread) => isSameId(thread.id, selectedThreadId)) || null,
    [threads, selectedThreadId]
  );
  const activeThreadParticipants = useMemo(
    () => (Array.isArray(activeThread?.participants) ? activeThread.participants.filter(Boolean) : []),
    [activeThread]
  );
  const activeThreadCanReply = activeThread?.can_reply ?? true;

  const resolveThreadDisplayParticipant = useCallback(
    (thread) => {
      if (!thread) return null;
      const participants = Array.isArray(thread.participants)
        ? thread.participants.filter(Boolean)
        : [];

      if (participants.length > 2) return null;

      const referenceAttendantId =
        isAdmin && adminViewMode === 'all' && selectedAdminAttendantId
          ? selectedAdminAttendantId
          : attendantId;
      const otherParticipant = participants.find(
        (participant) => !isSameId(participant?.id, referenceAttendantId)
      );

      return otherParticipant || thread?.participant || participants[0] || null;
    },
    [adminViewMode, attendantId, isAdmin, selectedAdminAttendantId]
  );

  const activeParticipant = useMemo(
    () => resolveThreadDisplayParticipant(activeThread),
    [activeThread, resolveThreadDisplayParticipant]
  );

  useEffect(() => {
    if (!isFloating) return;
    if (!selectedThreadId) {
      setCompactView('list');
    }
  }, [isFloating, selectedThreadId]);

  const activeParticipantPhoto = useMemo(() => {
    if (!activeParticipant) return '';
    const raw = resolveAttendantPhoto(activeParticipant);
    return raw ? resolveMediaUrl(raw) : '';
  }, [activeParticipant]);

  const resolveThreadTitle = useCallback(
    (thread) => {
      if (!thread) return 'Atendente';
      const participants = Array.isArray(thread.participants) ? thread.participants.filter(Boolean) : [];
      if (participants.length > 2) {
        const groupTitle =
          thread.title ||
          thread.name ||
          thread.group_name ||
          thread.groupName ||
          '';
        if (typeof groupTitle === 'string' && groupTitle.trim()) return groupTitle.trim();
        return participants
          .map((participant) => participant?.name || `Atendente #${participant?.id}`)
          .join('  x  ');
      }
      return resolveAttendantDisplayName(resolveThreadDisplayParticipant(thread));
    },
    [resolveThreadDisplayParticipant]
  );

  const activeParticipantName = useMemo(
    () => resolveThreadTitle(activeThread),
    [activeThread, resolveThreadTitle]
  );

  const activeStatusLabel = useMemo(() => {
    if (!activeThread) return '';
    if (activeThreadParticipants.length > 2) {
      const onlineCount = activeThreadParticipants.filter(
        (participant) =>
          resolveParticipantPresence(internalChatPresence, participant.id) ??
          participant?.is_online
      ).length;
      if (onlineCount === activeThreadParticipants.length) return 'Todos online';
      if (onlineCount > 0) return `${onlineCount} online`;
      return 'Todos offline';
    }
    const isOnline =
      resolveParticipantPresence(internalChatPresence, activeParticipant?.id) ??
      activeParticipant?.is_online ??
      false;
    return isOnline ? 'Online' : 'Offline';
  }, [activeParticipant, activeThread, activeThreadParticipants, internalChatPresence]);

  const activeStatusIsOnline = useMemo(() => {
    if (!activeThread) return false;
    if (activeThreadParticipants.length > 2) {
      return activeThreadParticipants.some(
        (participant) =>
          resolveParticipantPresence(internalChatPresence, participant.id) ??
          participant?.is_online
      );
    }
    return Boolean(
      resolveParticipantPresence(internalChatPresence, activeParticipant?.id) ??
        activeParticipant?.is_online
    );
  }, [activeParticipant, activeThread, activeThreadParticipants, internalChatPresence]);

  const activeThreadLastActivityAt =
    activeThread?.last_message_at ||
    activeThread?.created_at ||
    null;

  const isSelectedFileImage = useMemo(() => {
    if (!selectedFile) return false;
    if (selectedFile.type?.startsWith?.('image/')) return true;
    return isImageFile(selectedFile.name || '');
  }, [selectedFile]);

  const isSelectedFileAudio = useMemo(() => {
    if (!selectedFile) return false;
    if (selectedFile.type?.startsWith?.('audio/')) return true;
    return isAudioFile(selectedFile.name || '');
  }, [selectedFile]);

  const isSelectedFileVideo = useMemo(() => {
    if (!selectedFile) return false;
    if (selectedFile.type?.startsWith?.('video/')) return true;
    return isVideoFile(selectedFile.name || '');
  }, [selectedFile]);

  const selectedFilePreviewUrl = useMemo(() => {
    if (!selectedFile) return null;
    if (!isSelectedFileImage && !isSelectedFileVideo) return null;
    return URL.createObjectURL(selectedFile);
  }, [selectedFile, isSelectedFileImage, isSelectedFileVideo]);

  const recordedAudioUrl = useMemo(
    () => (recordedAudio ? URL.createObjectURL(recordedAudio) : null),
    [recordedAudio]
  );

  useEffect(() => {
    if (!selectedFilePreviewUrl) return undefined;
    return () => {
      try {
        URL.revokeObjectURL(selectedFilePreviewUrl);
      } catch {
        // URL pode ja ter sido revogada.
      }
    };
  }, [selectedFilePreviewUrl]);

  useEffect(() => {
    if (!recordedAudioUrl) return undefined;
    return () => {
      try {
        URL.revokeObjectURL(recordedAudioUrl);
      } catch {
        // URL pode ja ter sido revogada.
      }
    };
  }, [recordedAudioUrl]);

  useEffect(() => {
    if (!imageViewer || typeof document === 'undefined') return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setImageViewer(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [imageViewer]);

  useEffect(() => {
    setImageViewer(null);
  }, [selectedThreadId]);

  useEffect(() => {
    if (activeThreadCanReply) return;
    dragDepthRef.current = 0;
    setIsDraggingImage(false);
  }, [activeThreadCanReply]);

  const isMessageListNearBottom = useCallback(() => {
    const container = messageListRef.current;
    if (!container) return true;
    const distanceToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceToBottom <= SIDEBAR_SCROLL_THRESHOLD_PX;
  }, []);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior });
    }
  }, []);

  const applySelectedFile = useCallback((file, { clearInput = true } = {}) => {
    if (!file) return false;
    if (!validateFileSize(file)) return false;
    setSelectedFile(file);
    setRecordedAudio(null);
    setError(null);
    if (clearInput && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    return true;
  }, []);

  const upsertThread = useCallback((threadSummary) => {
    setThreads((prev) => {
      const filtered = prev.filter((thread) => thread.id !== threadSummary.id);
      return [threadSummary, ...filtered].sort(compareByActivityDesc);
    });
  }, []);

  const loadThreads = useCallback(async ({ offset = 0, append = false, reconcileUnread = false } = {}) => {
    if (missingAuth) return;
    if (isAdmin && adminViewMode === 'all' && !selectedAdminAttendantId) {
      setThreads([]);
      setThreadsOffset(0);
      setThreadsHasMore(false);
      setThreadsError(null);
      setThreadsLoading(false);
      return;
    }
    const requestSeq = threadsRequestSeqRef.current + 1;
    threadsRequestSeqRef.current = requestSeq;
    const startedAt = typeof performance !== 'undefined' ? performance.now() : null;
    try {
      setThreadsLoading(true);
      setThreadsError(null);
      const threadFilterOptions =
        isAdmin && adminViewMode === 'all'
          ? {
              participantAttendantId: selectedAdminAttendantId,
              limit: THREADS_PAGE_SIZE,
              offset,
            }
          : isAdmin && attendantId
            ? {
                participantAttendantId: attendantId,
                limit: THREADS_PAGE_SIZE,
                offset,
              }
            : { limit: THREADS_PAGE_SIZE, offset };
      const data = await listInternalChatThreadsDeduped(threadFilterOptions);
      if (threadsRequestSeqRef.current !== requestSeq) return;
      const nextThreads = (Array.isArray(data?.threads) ? data.threads : [])
        .slice()
        .sort(compareByActivityDesc);
      if (reconcileUnread && companyId && !isFloating) {
        const previousThreadsById = new Map(
          (threadsRef.current || []).map((thread) => [String(thread.id), thread])
        );
        nextThreads.forEach((thread) => {
          if (!thread?.id || isSameId(thread.id, selectedThreadIdRef.current)) return;
          const previous = previousThreadsById.get(String(thread.id));
          if (!previous) return;
          const nextActivity = safeTimestamp(thread.last_message_at, null);
          const previousActivity = safeTimestamp(previous.last_message_at, null);
          if (nextActivity === null || previousActivity === null || nextActivity <= previousActivity) {
            return;
          }
          const senderId = Number(thread.last_sender_id ?? thread.lastSenderId);
          if (Number.isFinite(senderId) && Number.isFinite(attendantId) && senderId === attendantId) {
            return;
          }
          incrementInternalChatUnreadThread(
            companyId,
            attendantId,
            thread.id,
            1,
            { source: 'internal-chat-view:reconcile-threads' }
          );
        });
      }
      setThreads((prev) => {
        if (!append) return nextThreads;
        const merged = new Map(prev.map((thread) => [String(thread.id), thread]));
        nextThreads.forEach((thread) => {
          if (thread?.id != null) {
            merged.set(String(thread.id), thread);
          }
        });
        return Array.from(merged.values()).sort(compareByActivityDesc);
      });
      setThreadsOffset(
        Number.isFinite(Number(data?.next_offset))
          ? Number(data.next_offset)
          : offset + nextThreads.length
      );
      setThreadsHasMore(Boolean(data?.has_more));
      if (!append && threadsLoadTriggerRef.current?.startedAt != null) {
        logInternalChatPerf(
          'threads ready after attendant context',
          threadsLoadTriggerRef.current.startedAt,
          {
            source: threadsLoadTriggerRef.current.source,
            attendantId: threadsLoadTriggerRef.current.attendantId,
            count: nextThreads.length,
          }
        );
        threadsLoadTriggerRef.current = null;
      }
      if (startedAt !== null) {
        logInternalChatPerf('threads loaded', startedAt, {
          count: nextThreads.length,
          offset,
          append,
          hasMore: Boolean(data?.has_more),
        });
      }
    } catch (err) {
      if (threadsRequestSeqRef.current !== requestSeq) return;
      console.error('Falha ao carregar lista de conversas do chat interno', {
        message: err?.message,
        status: err?.status ?? null,
        endpoint: err?.endpoint ?? null,
      });
      reportInternalChatClientError({
        source: 'front.internal-chat.view',
        scope: 'load threads failed',
        message: err?.message || 'Falha ao carregar lista de conversas',
        level: 'error',
        status: err?.status ?? null,
        endpoint: err?.endpoint ?? null,
      });
      setThreadsError(err?.message || 'Não foi possível carregar as conversas.');
    } finally {
      if (threadsRequestSeqRef.current === requestSeq) {
        setThreadsLoading(false);
      }
    }
  }, [adminViewMode, attendantId, companyId, isAdmin, isFloating, missingAuth, selectedAdminAttendantId]);

  const loadDirectoryAttendants = useCallback(
    async ({ search = directorySearchTerm, offset = 0, append = false } = {}) => {
      if (missingAuth) return;
      if (isAdmin && adminViewMode === 'all') {
        setDirectoryAttendants([]);
        setDirectoryError(null);
        setDirectoryLoading(false);
        setDirectoryOffset(0);
        setDirectoryHasMore(false);
        return;
      }

      const requestSeq = directoryRequestSeqRef.current + 1;
      directoryRequestSeqRef.current = requestSeq;
      const startedAt = typeof performance !== 'undefined' ? performance.now() : null;
      try {
        setDirectoryLoading(true);
        setDirectoryError(null);
        const data = await listInternalChatAttendants({
          search,
          limit: ATTENDANTS_DIRECTORY_PAGE_SIZE,
          offset,
        });
        if (directoryRequestSeqRef.current !== requestSeq) return;
        const attendants = Array.isArray(data?.attendants) ? data.attendants : [];
        setDirectoryAttendants((prev) => {
          if (!append) return attendants;
          const merged = new Map(prev.map((item) => [String(item.id), item]));
          attendants.forEach((item) => {
            if (item?.id != null) {
              merged.set(String(item.id), item);
            }
          });
          return Array.from(merged.values());
        });
        setDirectoryOffset(offset + attendants.length);
        setDirectoryHasMore(attendants.length === ATTENDANTS_DIRECTORY_PAGE_SIZE);
        if (startedAt !== null) {
          logInternalChatPerf('directory attendants loaded', startedAt, {
            count: attendants.length,
            offset,
            append,
            hasMore: attendants.length === ATTENDANTS_DIRECTORY_PAGE_SIZE,
          });
        }
      } catch (err) {
        if (directoryRequestSeqRef.current !== requestSeq) return;
        console.error('Falha ao carregar atendentes do chat interno', {
          message: err?.message,
          status: err?.status ?? null,
          endpoint: err?.endpoint ?? null,
        });
        setDirectoryError(err?.message || 'Não foi possível carregar os atendentes.');
        if (!append) {
          setDirectoryAttendants([]);
          setDirectoryOffset(0);
          setDirectoryHasMore(false);
        }
      } finally {
        if (directoryRequestSeqRef.current === requestSeq) {
          setDirectoryLoading(false);
        }
      }
    },
    [adminViewMode, directorySearchTerm, isAdmin, missingAuth]
  );

  const loadAdminDirectoryAttendants = useCallback(
    async ({ search = adminDirectorySearchTerm, offset = 0, append = false } = {}) => {
      if (missingAuth || !isAdmin || adminViewMode !== 'all') return;

      const requestSeq = adminDirectoryRequestSeqRef.current + 1;
      adminDirectoryRequestSeqRef.current = requestSeq;
      const startedAt = typeof performance !== 'undefined' ? performance.now() : null;
      try {
        setAdminDirectoryLoading(true);
        setAdminDirectoryError(null);
        const data = await listInternalChatAttendants({
          search,
          limit: ATTENDANTS_DIRECTORY_PAGE_SIZE,
          offset,
        });
        if (adminDirectoryRequestSeqRef.current !== requestSeq) return;
        const attendants = Array.isArray(data?.attendants) ? data.attendants : [];
        setAdminDirectoryAttendants((prev) => {
          if (!append) return attendants;
          const merged = new Map(prev.map((item) => [String(item.id), item]));
          attendants.forEach((item) => {
            if (item?.id != null) {
              merged.set(String(item.id), item);
            }
          });
          return Array.from(merged.values());
        });
        setAdminDirectoryOffset(offset + attendants.length);
        setAdminDirectoryHasMore(attendants.length === ATTENDANTS_DIRECTORY_PAGE_SIZE);
        if (startedAt !== null) {
          logInternalChatPerf('admin directory attendants loaded', startedAt, {
            count: attendants.length,
            offset,
            append,
            hasMore: attendants.length === ATTENDANTS_DIRECTORY_PAGE_SIZE,
          });
        }
      } catch (err) {
        if (adminDirectoryRequestSeqRef.current !== requestSeq) return;
        console.error('Falha ao carregar filtro de atendentes do admin no chat interno', {
          message: err?.message,
          status: err?.status ?? null,
          endpoint: err?.endpoint ?? null,
        });
        setAdminDirectoryError(err?.message || 'Não foi possível carregar os atendentes.');
        if (!append) {
          setAdminDirectoryAttendants([]);
          setAdminDirectoryOffset(0);
          setAdminDirectoryHasMore(false);
        }
      } finally {
        if (adminDirectoryRequestSeqRef.current === requestSeq) {
          setAdminDirectoryLoading(false);
        }
      }
    },
    [adminDirectorySearchTerm, adminViewMode, isAdmin, missingAuth]
  );

  const loadMessages = useCallback(
    async (threadId, { beforeId = null, appendOlder = false } = {}) => {
      const requestSeq = messagesRequestSeqRef.current + 1;
      messagesRequestSeqRef.current = requestSeq;
      const startedAt = typeof performance !== 'undefined' ? performance.now() : null;
      const isCurrentRequest = () => (
        messagesRequestSeqRef.current === requestSeq &&
        isSameId(selectedThreadIdRef.current, threadId)
      );
      try {
        if (appendOlder) {
          setLoadingOlderMessages(true);
        } else {
          setMessagesLoading(true);
          setMessagesError(null);
        }
        const data = await getInternalChatThreadMessages(threadId, {
          beforeId,
          limit: DEFAULT_HISTORY_LIMIT,
        });
        if (!isCurrentRequest()) return;
        const nextItems = dedupeAndSortMessages(data?.items);
        setMessages((prev) => {
          if (!appendOlder) {
            const messagesReceivedWhileLoading = prev.filter((message) =>
              isSameId(resolveInternalMessageThreadId(message), threadId)
            );
            return dedupeAndSortMessages([...nextItems, ...messagesReceivedWhileLoading]);
          }
          return dedupeAndSortMessages([...nextItems, ...prev]);
        });
        setMessagesOldestId(
          Number.isFinite(Number(data?.oldest_id))
            ? Number(data.oldest_id)
            : (nextItems[0]?.id ?? null)
        );
        setMessagesHasOlder(Boolean(data?.has_older));
        if (
          !appendOlder &&
          isSameId(threadSelectionStartRef.current?.threadId, threadId) &&
          threadSelectionStartRef.current.startedAt != null
        ) {
          logInternalChatPerf(
            'thread selected',
            threadSelectionStartRef.current.startedAt,
            {
              source: threadSelectionStartRef.current.source,
              threadId,
              count: nextItems.length,
            }
          );
          threadSelectionStartRef.current = null;
        }
        if (startedAt !== null) {
          logInternalChatPerf('messages loaded', startedAt, {
            threadId,
            count: nextItems.length,
            limit: DEFAULT_HISTORY_LIMIT,
            beforeId,
            appendOlder,
            hasOlder: Boolean(data?.has_older),
          });
        }
      } catch (err) {
        if (!isCurrentRequest()) return;
        console.error('Falha ao carregar mensagens da conversa interna', {
          threadId,
          message: err?.message,
          status: err?.status ?? null,
          endpoint: err?.endpoint ?? null,
        });
        reportInternalChatClientError({
          source: 'front.internal-chat.view',
          scope: 'load messages failed',
          message: err?.message || 'Falha ao carregar mensagens da conversa interna',
          level: 'error',
          status: err?.status ?? null,
          endpoint: err?.endpoint ?? null,
          meta: { threadId },
        });
        if (!appendOlder) {
          setMessagesOldestId(null);
          setMessagesHasOlder(false);
        }
        pendingOlderMessagesScrollRef.current = null;
        threadSelectionStartRef.current = null;
        setMessagesError(err?.message || 'Não foi possível carregar as mensagens.');
      } finally {
        if (isCurrentRequest()) {
          if (appendOlder) {
            setLoadingOlderMessages(false);
          } else {
            setMessagesLoading(false);
          }
        }
      }
    },
    []
  );

  const syncPendingInternalChatData = useCallback(
    async ({ reason = 'manual' } = {}) => {
      if (missingAuth) return;
      loadThreads({ offset: 0, append: false, reconcileUnread: true });

      const activeThreadId = selectedThreadIdRef.current;
      if (!activeThreadId) return;

      const threadMessages = (messagesRef.current || []).filter((message) =>
        isSameId(resolveInternalMessageThreadId(message), activeThreadId)
      );
      const newestMessageId = threadMessages.reduce((maxId, message) => {
        const parsed = Number(message?.id);
        return Number.isFinite(parsed) && parsed > maxId ? parsed : maxId;
      }, 0);

      if (!newestMessageId) {
        loadMessages(activeThreadId);
        return;
      }

      try {
        const data = await getInternalChatThreadMessages(activeThreadId, {
          sinceId: newestMessageId,
          limit: DEFAULT_HISTORY_LIMIT,
        });
        if (!isSameId(selectedThreadIdRef.current, activeThreadId)) return;
        const nextItems = dedupeAndSortMessages(data?.items);
        if (!nextItems.length) return;
        const shouldAutoScroll = isMessageListNearBottom();
        setMessages((prev) => {
          pendingScrollBehaviorRef.current = shouldAutoScroll ? 'smooth' : null;
          return dedupeAndSortMessages([...prev, ...nextItems]);
        });
        if (companyId) {
          clearInternalChatUnreadThreads(
            companyId,
            attendantId,
            [activeThreadId],
            { source: 'internal-chat-view:reconcile-active-thread' }
          );
          markThreadReadOnServer(activeThreadId, nextItems.at(-1)?.id ?? null);
        }
      } catch (err) {
        console.error('Falha ao reconciliar mensagens pendentes do chat interno', {
          reason,
          threadId: activeThreadId,
          message: err?.message,
          status: err?.status ?? null,
          endpoint: err?.endpoint ?? null,
        });
      }
    },
    [
      attendantId,
      companyId,
      isMessageListNearBottom,
      loadMessages,
      loadThreads,
      markThreadReadOnServer,
      missingAuth,
    ]
  );

  const ensureThread = useCallback(
    async (targetId) => {
      if (missingAuth) return null;
      let thread = threadByParticipant.get(targetId);
      if (thread) return thread;

      try {
        const created = await createInternalChatThread(targetId);
        upsertThread(created);
        return created;
      } catch (err) {
        setError(err?.message || 'Não foi possível iniciar a conversa.');
        throw err;
      }
    },
    [threadByParticipant, upsertThread, missingAuth]
  );

  const handleSelectAttendant = useCallback(
    async (attendantTargetId, { source = 'manual' } = {}) => {
      if (missingAuth) return;
      const startedAt = typeof performance !== 'undefined' ? performance.now() : null;
      setSelectedAttendantId(attendantTargetId);
      try {
        const thread = await ensureThread(attendantTargetId);
        if (startedAt !== null) {
          logInternalChatPerf('attendant selected', startedAt, {
            source,
            attendantTargetId,
            threadId: thread.id,
          });
          threadSelectionStartRef.current = {
            startedAt,
            threadId: thread.id,
            source: `${source}:thread-open`,
          };
        }
        setSelectedThreadId(thread.id);
        if (isFloating) {
          setCompactView('conversation');
        }
      } catch {
        // erro já tratado em ensureThread
      }
    },
    [ensureThread, isFloating, missingAuth]
  );

  useEffect(() => {
    if (missingAuth || !requestedTargetId) return;
    const requestKey = `${requestedTargetId}:${requestedPrefill || ''}`;
    if (processedRouteTargetKeyRef.current === requestKey) {
      return;
    }

    processedRouteTargetKeyRef.current = requestKey;

    handleSelectAttendant(requestedTargetId, { source: 'route-target' });
    if (requestedPrefill) {
      setInputValue((prev) => prev || requestedPrefill);
    }
  }, [handleSelectAttendant, requestedPrefill, requestedTargetId, missingAuth]);

  useEffect(() => {
    if (!focusRequest) return;

    const requestKey =
      focusRequest.nonce ??
      `${focusRequest?.targetAttendantId ?? focusRequest?.attendantId ?? focusRequest?.targetId ?? ''}:${focusRequest?.prefill ?? ''}`;

    if (processedFocusRequestNonceRef.current === requestKey) {
      return;
    }

    processedFocusRequestNonceRef.current = requestKey;

    const rawTarget =
      focusRequest?.targetAttendantId ??
      focusRequest?.attendantId ??
      focusRequest?.targetId ??
      null;
    const parsedTarget = Number(rawTarget);
    if (Number.isFinite(parsedTarget) && parsedTarget > 0) {
      handleSelectAttendant(parsedTarget, { source: 'focus-request' });
    }
    if (typeof focusRequest?.prefill === 'string' && focusRequest.prefill.trim()) {
      setInputValue((prev) => prev || focusRequest.prefill);
    }
  }, [focusRequest, handleSelectAttendant]);

  useEffect(() => {
    if (isFloating) return undefined;
    const handleFocusInternalChat = (event) => {
      const rawTarget =
        event?.detail?.attendantId ??
        event?.detail?.targetId ??
        null;
      if (rawTarget === null || rawTarget === undefined) return;
      const parsed = Number(rawTarget);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      handleSelectAttendant(parsed, { source: 'global-focus-event' });
      if (typeof event?.detail?.prefill === 'string' && event.detail.prefill.trim()) {
        setInputValue((prev) => prev || event.detail.prefill);
      }
    };

    window.addEventListener('bluezap:focus-internal-chat', handleFocusInternalChat);
    return () => {
      window.removeEventListener('bluezap:focus-internal-chat', handleFocusInternalChat);
    };
  }, [handleSelectAttendant, isFloating]);

  const handleIncomingThread = useCallback(
    (thread) => {
      if (!thread?.id) return;
      upsertThread(thread);
    },
    [upsertThread]
  );

  const updateThreadPreviewFromMessage = useCallback((message) => {
    const threadId = resolveInternalMessageThreadId(message);
    if (!threadId) return;

    const mediaLabel = resolveMediaLabel(
      message,
      message?.file_name || message?.fileName || '',
      message?.media_url || message?.mediaUrl || ''
    );
    const lastMessage =
      message?.content ??
      message?.message ??
      message?.text ??
      message?.body ??
      '';
    const resolvedLastMessage = lastMessage || mediaLabel || '';
    const lastMessageAt =
      message?.created_at ??
      message?.createdAt ??
      message?.timestamp ??
      null;
    const lastSenderId =
      message?.sender_id ??
      message?.senderId ??
      message?.sender?.id ??
      null;

    setThreads((prev) => {
      const existing = prev.find((thread) => isSameId(thread.id, threadId));
      if (!existing) return prev;
      const incomingTimestamp = safeTimestamp(lastMessageAt, null);
      const existingTimestamp = resolveThreadActivityMs(existing, 0);
      if (incomingTimestamp !== null && incomingTimestamp < existingTimestamp) {
        return prev;
      }

      const nextThread = {
        ...existing,
        last_message: resolvedLastMessage || existing.last_message || '',
        last_message_at:
          incomingTimestamp !== null
            ? lastMessageAt
            : existing.last_message_at || existing.created_at,
        last_sender_id: lastSenderId ?? existing.last_sender_id,
      };

      const filtered = prev.filter((thread) => !isSameId(thread.id, threadId));
      return [nextThread, ...filtered].sort(compareByActivityDesc);
    });
  }, []);

  const handleIncomingMessage = useCallback(
    (payload) => {
      if (!payload?.message) return;
      const messageThreadId = resolveInternalMessageThreadId(payload.message, payload);
      const incomingMessage = messageThreadId == null || payload.message.thread_id != null
        ? payload.message
        : { ...payload.message, thread_id: messageThreadId };
      if (payload.thread) {
        handleIncomingThread(payload.thread);
      } else {
        updateThreadPreviewFromMessage(incomingMessage);
      }
      const senderId = Number(
        incomingMessage.sender_id ?? incomingMessage.senderId ?? incomingMessage.sender?.id
      );
      const isSelfMessage =
        Number.isFinite(attendantId) && Number.isFinite(senderId) && senderId === attendantId;
      const isAdminObserverThread = payload?.thread?.can_reply === false;
      const shouldIgnoreUnreadByScope =
        isAdmin && adminViewMode !== 'all' && isAdminObserverThread;
      const shouldAutoScroll = isSelfMessage || isMessageListNearBottom();
      const activeThreadId = selectedThreadIdRef.current;
      if (isSameId(messageThreadId, activeThreadId)) {
        setMessages((prev) => {
          pendingScrollBehaviorRef.current = shouldAutoScroll ? 'smooth' : null;
          return dedupeAndSortMessages([...prev, incomingMessage]);
        });
        if (companyId && activeThreadId) {
          clearInternalChatUnreadThreads(
            companyId,
            attendantId,
            [activeThreadId],
            { source: 'internal-chat-view:active-thread' }
          );
          markThreadReadOnServer(activeThreadId, incomingMessage.id ?? null);
        }
      } else if (
        messageThreadId &&
        !isSelfMessage &&
        companyId &&
        !isFloating &&
        !shouldIgnoreUnreadByScope
      ) {
        incrementInternalChatUnreadThread(
          companyId,
          attendantId,
          messageThreadId,
          1,
          { source: 'internal-chat-view' }
        );
      }
    },
    [
      handleIncomingThread,
      updateThreadPreviewFromMessage,
      attendantId,
      adminViewMode,
      companyId,
      isAdmin,
      isMessageListNearBottom,
      isFloating,
      markThreadReadOnServer,
    ]
  );

  useEffect(() => {
    if (!shouldLoadThreads) {
      setThreads([]);
      setThreadsOffset(0);
      setThreadsHasMore(false);
      setThreadsError(null);
      setThreadsLoading(false);
      return undefined;
    }
    if (!isAdmin || adminViewMode === 'mine') {
      if (threadsLoadTriggerRef.current == null && attendantId) {
        threadsLoadTriggerRef.current = {
          startedAt: typeof performance !== 'undefined' ? performance.now() : null,
          attendantId,
          source: 'current-attendant-ready',
        };
      }
    }

    loadThreads({ offset: 0, append: false });
    return undefined;
  }, [adminViewMode, attendantId, isAdmin, loadThreads, shouldLoadThreads]);

  useEffect(() => {
    if (!shouldLoadMineDirectory) return;
    loadDirectoryAttendants({ search: directorySearchTerm, offset: 0, append: false });
  }, [directorySearchTerm, loadDirectoryAttendants, shouldLoadMineDirectory]);

  useEffect(() => {
    if (!shouldLoadAdminDirectory) return;
    loadAdminDirectoryAttendants({ search: adminDirectorySearchTerm, offset: 0, append: false });
  }, [
    adminDirectorySearchTerm,
    loadAdminDirectoryAttendants,
    shouldLoadAdminDirectory,
  ]);

  useEffect(() => {
    if (!selectedThreadId || !companyId) return;
    clearInternalChatUnreadThreads(
      companyId,
      attendantId,
      [selectedThreadId],
      { source: 'internal-chat-view:select-thread' }
    );
    markThreadReadOnServer(selectedThreadId);
  }, [attendantId, companyId, markThreadReadOnServer, selectedThreadId]);

  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
    messagesRequestSeqRef.current += 1;
    if (missingAuth || !selectedThreadId) {
      setMessages([]);
      setMessagesLoading(false);
      setMessagesError(null);
      setMessagesHasOlder(false);
      setMessagesOldestId(null);
      setLoadingOlderMessages(false);
      pendingScrollBehaviorRef.current = null;
      pendingOlderMessagesScrollRef.current = null;
      return;
    }
    setMessages([]);
    setMessagesHasOlder(false);
    setMessagesOldestId(null);
    setLoadingOlderMessages(false);
    pendingScrollBehaviorRef.current = 'auto';
    loadMessages(selectedThreadId);
  }, [selectedThreadId, loadMessages, missingAuth]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleInternalChatEvent = (event) => {
      const payload = event?.detail;
      if (payload?.type === 'sync_requested') {
        syncPendingInternalChatData({ reason: payload.reason || 'event' });
        return;
      }
      if (payload?.type === 'thread_created') {
        handleIncomingThread(payload.thread);
        return;
      }
      if (payload?.type === 'message') {
        handleIncomingMessage(payload);
      }
    };

    window.addEventListener(INTERNAL_CHAT_EVENT, handleInternalChatEvent);
    return () => {
      window.removeEventListener(INTERNAL_CHAT_EVENT, handleInternalChatEvent);
    };
  }, [handleIncomingMessage, handleIncomingThread, syncPendingInternalChatData]);

  useEffect(() => {
    if (typeof window === 'undefined' || missingAuth) return undefined;

    const handleOnline = () => {
      syncPendingInternalChatData({ reason: 'browser_online' });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncPendingInternalChatData({ reason: 'tab_visible' });
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [missingAuth, syncPendingInternalChatData]);

  useEffect(() => {
    const pendingOlderScroll = pendingOlderMessagesScrollRef.current;
    if (pendingOlderScroll) {
      const container = messageListRef.current;
      if (container) {
        const heightDelta = container.scrollHeight - pendingOlderScroll.previousHeight;
        container.scrollTop = pendingOlderScroll.previousTop + heightDelta;
      }
      pendingOlderMessagesScrollRef.current = null;
      return;
    }

    const behavior = pendingScrollBehaviorRef.current;
    if (!behavior || messages.length === 0) return;
    scrollToBottom(behavior);
    pendingScrollBehaviorRef.current = null;
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (readyMarkLoggedRef.current || missingAuth) return;
    if (isAdmin && adminViewMode === 'all' && isSidebarSettingsOpen && adminDirectoryLoading) return;
    if (initialLoadStartRef.current === null) return;

    logInternalChatPerf('screen ready', initialLoadStartRef.current, {
      threadsDeferred: shouldLoadThreads,
      directoryAttendants: directoryAttendants.length,
      adminViewMode,
    });
    readyMarkLoggedRef.current = true;
  }, [
    adminViewMode,
    adminDirectoryLoading,
    directoryAttendants.length,
    isAdmin,
    isSidebarSettingsOpen,
    missingAuth,
    shouldLoadThreads,
  ]);

  useEffect(() => {
    if (!isAdmin) {
      setAdminViewMode('mine');
      setSelectedAdminAttendantId(null);
      return;
    }
    if (adminViewMode === 'mine' && activeThread && activeThread.can_reply === false) {
      setSelectedThreadId(null);
      setMessages([]);
    }
  }, [activeThread, adminViewMode, isAdmin]);

  useEffect(() => {
    if (!isAdmin || adminViewMode !== 'all') return;
    if (selectedAdminAttendantId == null) {
      setSelectedThreadId(null);
      setMessages([]);
      setMessagesError(null);
    }
  }, [adminViewMode, isAdmin, selectedAdminAttendantId]);

  const handleSwitchToAdminMode = useCallback(() => {
    setAdminViewMode('all');
    setSelectedThreadId(null);
    setMessages([]);
  }, []);

  const handleSwitchToMineMode = useCallback(() => {
    setAdminViewMode('mine');
    setSelectedThreadId(null);
    setMessages([]);
  }, []);

  const handleSelectAdminAttendant = useCallback((attendant) => {
    const parsed = Number(attendant?.id);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setSelectedAdminAttendantId(null);
      setSelectedAdminAttendantName('');
      setSelectedThreadId(null);
      setMessages([]);
      return;
    }
    threadsLoadTriggerRef.current = {
      startedAt: typeof performance !== 'undefined' ? performance.now() : null,
      attendantId: parsed,
      source: 'admin-attendant-selection',
    };
    setSelectedAdminAttendantId(parsed);
    setSelectedAdminAttendantName(resolveAttendantDisplayName(attendant));
    setSelectedThreadId(null);
    setMessages([]);
  }, []);

  const handleClearAdminAttendantSelection = useCallback(() => {
    setSelectedAdminAttendantId(null);
    setSelectedAdminAttendantName('');
    setSelectedThreadId(null);
    setMessages([]);
  }, []);

  useEffect(() => {
    if (!activeThread || sending || !activeThreadCanReply) return undefined;
    const frame = requestAnimationFrame(() => {
      try {
        inputRef.current?.focus();
      } catch {
        // ignore focus errors
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [activeThread, activeThreadCanReply, sending]);

  useEffect(() => {
    return () => {
      try {
        mediaRecorderRef.current?.stream?.getTracks?.().forEach((track) => track.stop());
      } catch {
        // Stream ja pode ter sido encerrada.
      }
    };
  }, []);

  const handleSendRecordedAudio = async () => {
    if (sending || !selectedThreadId || !recordedAudio) return;
    if (!validateFileSize(recordedAudio)) {
      setRecordedAudio(null);
      return;
    }
    const text = inputValue.trim();
    try {
      setSending(true);
      setError(null);
      const mimeType = recordingMimeTypeRef.current || recordedAudio.type || 'audio/webm; codecs=opus';
      const stamp = Date.now();
      const fileName = `audio-${stamp}.${getAudioExtension(mimeType)}`;
      const audioFile = new File([recordedAudio], fileName, { type: mimeType });
      const created = await sendInternalChatThreadMessage(selectedThreadId, {
        content: text || undefined,
        file: audioFile,
      });
      setInputValue('');
      setRecordedAudio(null);
      if (created?.id) {
        const previewLabel = resolveMediaLabel(
          created,
          created?.file_name || '',
          created?.media_url || ''
        );
        const nextThread = activeThread
          ? {
              ...activeThread,
              last_message: created.content ?? previewLabel ?? activeThread.last_message ?? '',
              last_message_at: created.created_at ?? activeThread.last_message_at,
              last_sender_id: created.sender_id ?? activeThread.last_sender_id,
            }
          : null;
        handleIncomingMessage({ message: created, thread: nextThread });
      }
    } catch (err) {
      console.error('Falha ao enviar áudio no chat interno', {
        message: err?.message,
        status: err?.status ?? null,
        endpoint: err?.endpoint ?? null,
      });
      reportInternalChatClientError({
        source: 'front.internal-chat.view',
        scope: 'send recorded audio failed',
        message: err?.message || 'Falha ao enviar áudio no chat interno',
        level: 'error',
        status: err?.status ?? null,
        endpoint: err?.endpoint ?? null,
        meta: { threadId: selectedThreadId },
      });
      setError(err?.message || 'Não foi possível enviar o áudio.');
    } finally {
      setSending(false);
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (sending || !selectedThreadId) return;
    if (recordedAudio) {
      await handleSendRecordedAudio();
      return;
    }
    const text = inputValue.trim();
    if (!text && !selectedFile) return;
    try {
      setSending(true);
      setError(null);
      const created = await sendInternalChatThreadMessage(selectedThreadId, {
        content: text || undefined,
        file: selectedFile,
      });
      setInputValue('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (created?.id) {
        const previewLabel = resolveMediaLabel(
          created,
          created?.file_name || '',
          created?.media_url || ''
        );
        const nextThread = activeThread
          ? {
              ...activeThread,
              last_message: created.content ?? previewLabel ?? activeThread.last_message ?? '',
              last_message_at: created.created_at ?? activeThread.last_message_at,
              last_sender_id: created.sender_id ?? activeThread.last_sender_id,
            }
          : null;
        handleIncomingMessage({ message: created, thread: nextThread });
      }
    } catch (err) {
      console.error('Falha ao enviar mensagem no chat interno', {
        message: err?.message,
        status: err?.status ?? null,
        endpoint: err?.endpoint ?? null,
      });
      reportInternalChatClientError({
        source: 'front.internal-chat.view',
        scope: 'send message failed',
        message: err?.message || 'Falha ao enviar mensagem no chat interno',
        level: 'error',
        status: err?.status ?? null,
        endpoint: err?.endpoint ?? null,
        meta: { threadId: selectedThreadId },
      });
      setError(err?.message || 'Não foi possível enviar a mensagem.');
    } finally {
      setSending(false);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    applySelectedFile(file, { clearInput: false });
    if (event.target) {
      event.target.value = '';
    }
  };

  const handlePasteImage = useCallback(
    (event) => {
      if (sending) return;
      const items = event.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.kind === 'file' && item.type?.startsWith('image/')) {
          const file = item.getAsFile();
          if (!file) continue;
          event.preventDefault();
          const wrapped = new File([file], file.name || `pasted-image-${Date.now()}`, {
            type: file.type,
          });
          if (!applySelectedFile(wrapped)) return;
          break;
        }
      }
    },
    [applySelectedFile, sending]
  );

  const isImageTransfer = useCallback((dataTransfer) => {
    const files = Array.from(dataTransfer?.files || []);
    if (files.some((file) => file?.type?.startsWith?.('image/'))) {
      return true;
    }
    const items = Array.from(dataTransfer?.items || []);
    return items.some((item) => item.kind === 'file' && item.type?.startsWith?.('image/'));
  }, []);

  const extractDraggedImageFile = useCallback((dataTransfer) => {
    const directFile = Array.from(dataTransfer?.files || []).find(
      (file) => file?.type?.startsWith?.('image/')
    );
    if (directFile) return directFile;
    const imageItem = Array.from(dataTransfer?.items || []).find(
      (item) => item.kind === 'file' && item.type?.startsWith?.('image/')
    );
    return imageItem?.getAsFile?.() || null;
  }, []);

  const handlePanelDragEnter = useCallback(
    (event) => {
      if (!selectedThreadId || !activeThreadCanReply || sending || !isImageTransfer(event.dataTransfer)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDraggingImage(true);
    },
    [activeThreadCanReply, isImageTransfer, selectedThreadId, sending]
  );

  const handlePanelDragOver = useCallback(
    (event) => {
      if (!selectedThreadId || !activeThreadCanReply || sending || !isImageTransfer(event.dataTransfer)) return;
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
      setIsDraggingImage(true);
    },
    [activeThreadCanReply, isImageTransfer, selectedThreadId, sending]
  );

  const handlePanelDragLeave = useCallback(
    (event) => {
      if (!isImageTransfer(event.dataTransfer)) return;
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) {
        setIsDraggingImage(false);
      }
    },
    [isImageTransfer]
  );

  const handlePanelDrop = useCallback(
    (event) => {
      if (!selectedThreadId || !activeThreadCanReply || sending) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDraggingImage(false);
      const file = extractDraggedImageFile(event.dataTransfer);
      if (!file) {
        setError('Arraste uma imagem para anexar no chat interno.');
        return;
      }
      applySelectedFile(file);
    },
    [activeThreadCanReply, applySelectedFile, extractDraggedImageFile, selectedThreadId, sending]
  );

  const handleToggleRecording = async () => {
    if (sending) return;
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const preferredMimeTypes = [
          'audio/ogg; codecs=opus',
          'audio/webm; codecs=opus',
        ];
        const supportedMimeType = preferredMimeTypes.find(
          (type) => typeof MediaRecorder !== 'undefined'
            && typeof MediaRecorder.isTypeSupported === 'function'
            && MediaRecorder.isTypeSupported(type),
        );

        const mediaRecorderOptions = supportedMimeType ? { mimeType: supportedMimeType } : undefined;
        const mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];
        setRecordedAudio(null);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        const effectiveMimeType = mediaRecorder.mimeType
          || supportedMimeType
          || 'audio/webm; codecs=opus';
        recordingMimeTypeRef.current = effectiveMimeType;
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            audioChunksRef.current.push(e.data);
          }
        };
        mediaRecorder.onstop = () => {
          setIsRecording(false);
          const blob = new Blob(audioChunksRef.current, { type: recordingMimeTypeRef.current || 'audio/webm; codecs=opus' });
          setRecordedAudio(blob);
          try {
            mediaRecorder.stream.getTracks().forEach((track) => track.stop());
          } catch {
            // Stream ja pode ter sido encerrada.
          }
        };
        mediaRecorder.start();
        setIsRecording(true);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Não foi possível acessar o microfone.');
      }
    } else {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
    }
  };

  const handleCancelRecordedAudio = () => {
    setRecordedAudio(null);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSelectThread = useCallback((threadId) => {
    if (Number(threadId) === Number(selectedThreadId)) return;
    threadSelectionStartRef.current = {
      startedAt: typeof performance !== 'undefined' ? performance.now() : null,
      threadId,
      source: 'manual-thread-selection',
    };
    setSelectedThreadId(threadId);
    setSelectedAttendantId(null);
    if (isFloating) {
      setCompactView('conversation');
    }
  }, [isFloating, selectedThreadId]);

  const handleLoadOlderMessages = useCallback(() => {
    if (
      !selectedThreadId ||
      loadingOlderMessages ||
      messagesLoading ||
      !messagesHasOlder ||
      !messagesOldestId
    ) {
      return;
    }
    const container = messageListRef.current;
    pendingOlderMessagesScrollRef.current = container
      ? {
          previousHeight: container.scrollHeight,
          previousTop: container.scrollTop,
        }
      : null;
    loadMessages(selectedThreadId, {
      beforeId: messagesOldestId,
      appendOlder: true,
    });
  }, [
    loadingOlderMessages,
    loadMessages,
    messagesHasOlder,
    messagesLoading,
    messagesOldestId,
    selectedThreadId,
  ]);

  const handleLoadMoreAttendants = useCallback(() => {
    if (directoryLoading || !directoryHasMore) return;
    loadDirectoryAttendants({
      search: directorySearchTerm,
      offset: directoryOffset,
      append: true,
    });
  }, [directoryHasMore, directoryLoading, directoryOffset, directorySearchTerm, loadDirectoryAttendants]);

  const handleLoadMoreAdminAttendants = useCallback(() => {
    if (adminDirectoryLoading || !adminDirectoryHasMore) return;
    loadAdminDirectoryAttendants({
      search: adminDirectorySearchTerm,
      offset: adminDirectoryOffset,
      append: true,
    });
  }, [
    adminDirectoryHasMore,
    adminDirectoryLoading,
    adminDirectoryOffset,
    adminDirectorySearchTerm,
    loadAdminDirectoryAttendants,
  ]);

  const handleLoadMoreThreads = useCallback(() => {
    if (threadsLoading || !threadsHasMore) return;
    loadThreads({
      offset: threadsOffset,
      append: true,
    });
  }, [loadThreads, threadsHasMore, threadsLoading, threadsOffset]);

  const growSidebarRenderLimit = useCallback(() => {
    setSidebarRenderLimit((prev) => prev + SIDEBAR_RENDER_BATCH);
  }, []);

  const handleSidebarScroll = useCallback(() => {
    const container = sidebarListRef.current;
    if (!container) return;

    const distanceToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceToBottom > SIDEBAR_SCROLL_THRESHOLD_PX) return;

    if (hasHiddenRenderedAdminThreads || hasHiddenRenderedAttendants) {
      growSidebarRenderLimit();
      return;
    }

    if (threadsHasMore && !threadsLoading) {
      handleLoadMoreThreads();
      return;
    }

    if ((!isAdmin || adminViewMode === 'mine') && directoryHasMore && !directoryLoading) {
      handleLoadMoreAttendants();
    }
  }, [
    adminViewMode,
    directoryHasMore,
    directoryLoading,
    growSidebarRenderLimit,
    handleLoadMoreThreads,
    handleLoadMoreAttendants,
    hasHiddenRenderedAdminThreads,
    hasHiddenRenderedAttendants,
    isAdmin,
    threadsHasMore,
    threadsLoading,
  ]);

  useEffect(() => {
    const container = sidebarListRef.current;
    if (!container) return;

    const distanceToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceToBottom > SIDEBAR_SCROLL_THRESHOLD_PX) return;

    if (hasHiddenRenderedAdminThreads || hasHiddenRenderedAttendants) {
      growSidebarRenderLimit();
      return;
    }

    if (threadsHasMore && !threadsLoading) {
      handleLoadMoreThreads();
      return;
    }

    if ((!isAdmin || adminViewMode === 'mine') && directoryHasMore && !directoryLoading) {
      handleLoadMoreAttendants();
    }
  }, [
    adminViewMode,
    directoryHasMore,
    directoryLoading,
    displayedAdminThreads.length,
    displayedAttendants.length,
    growSidebarRenderLimit,
    handleLoadMoreThreads,
    handleLoadMoreAttendants,
    hasHiddenRenderedAdminThreads,
    hasHiddenRenderedAttendants,
    isAdmin,
    threadsHasMore,
    threadsLoading,
  ]);

  const renderAttendantRow = (attendant) => {
    const thread = attendant.thread;
    const isActive =
      (thread && isSameId(thread.id, selectedThreadId)) ||
      isSameId(attendant.id, selectedAttendantId);
    const isOnline = attendant.isOnline;
    const displayName = attendant.displayName || resolveAttendantDisplayName(attendant);
    const lastMessageText = thread?.last_message
      ? `${thread.last_sender_id === attendantId ? 'Você: ' : ''}${thread.last_message}`
      : 'Nenhuma conversa ainda';
    const lastMessageAt = thread?.last_message_at ? formatTimestamp(thread.last_message_at) : '';
    const unreadCount = thread?.id ? privateUnreadMap[thread.id] : 0;

    return (
      <button
        key={attendant.id}
        type="button"
        className={`internal-chat-attendant ${isActive ? 'is-active' : ''}`}
        onClick={() => handleSelectAttendant(attendant.id)}
        style={{ position: 'relative' }}
      >
        <div className="internal-chat-attendant__profile">
          <Avatar
            name={displayName}
            photo={attendant.photo}
            size={44}
            className="internal-chat-attendant__avatar"
          />
          <div className="internal-chat-attendant__body">
            <div className="internal-chat-attendant__head">
              <div className="internal-chat-attendant__name">
                <span className={`presence-dot ${isOnline ? 'is-online' : 'is-offline'}`} />
                {displayName}
              </div>
            </div>
            <div className="internal-chat-attendant__preview">
              <span className="internal-chat-attendant__preview-text">{lastMessageText}</span>
              {lastMessageAt && (
                <small className="internal-chat-attendant__time">{lastMessageAt}</small>
              )}
            </div>
          </div>
        </div>
        {thread?.id && unreadCount > 0 && (
          <span
            className="internal-chat-attendant__badge"
            title={`Novas mensagens: ${unreadCount}`}
            aria-label={`Novas mensagens: ${unreadCount}`}
          >
            {unreadCount}
          </span>
        )}
      </button>
    );
  };

  const renderAdminThreadRow = (thread) => {
    const title = resolveThreadTitle(thread);
    const isActive = isSameId(thread.id, selectedThreadId);
    const lastMessageText = thread?.last_message || 'Sem histórico ainda';
    const lastMessageAt = thread?.last_message_at ? formatTimestamp(thread.last_message_at) : '';
    const unreadCount = thread?.id ? privateUnreadMap[thread.id] : 0;
    const participants = Array.isArray(thread.participants) ? thread.participants : [];
    const displayParticipant = resolveThreadDisplayParticipant(thread);
    const displayParticipantPhoto = displayParticipant
      ? resolveAttendantPhoto(displayParticipant)
      : '';
    const onlineCount = participants.filter(
      (participant) =>
        resolveParticipantPresence(internalChatPresence, participant.id) ??
        participant?.is_online
    ).length;
    const isIndividualOnline = Boolean(
      resolveParticipantPresence(internalChatPresence, displayParticipant?.id) ??
        displayParticipant?.is_online
    );
    const hasOnlineParticipant = participants.length > 2 ? onlineCount > 0 : isIndividualOnline;

    return (
      <button
        key={thread.id}
        type="button"
        className={`internal-chat-attendant ${isActive ? 'is-active' : ''}`}
        onClick={() => handleSelectThread(thread.id)}
        style={{ position: 'relative' }}
      >
        <div className="internal-chat-attendant__profile">
          <Avatar
            name={title}
            photo={displayParticipantPhoto}
            size={44}
            className="internal-chat-attendant__avatar"
          />
          <div className="internal-chat-attendant__body">
            <div className="internal-chat-attendant__head">
              <div className="internal-chat-attendant__name">
                <span className={`presence-dot ${hasOnlineParticipant ? 'is-online' : 'is-offline'}`} />
                {title}
              </div>
            </div>
            <div className="internal-chat-attendant__preview">
              <span className="internal-chat-attendant__preview-text">{lastMessageText}</span>
              {lastMessageAt && (
                <small className="internal-chat-attendant__time">{lastMessageAt}</small>
              )}
            </div>
          </div>
        </div>
        {thread?.id && unreadCount > 0 && (
          <span
            className="internal-chat-attendant__badge"
            title={`Novas mensagens: ${unreadCount}`}
            aria-label={`Novas mensagens: ${unreadCount}`}
          >
            {unreadCount}
          </span>
        )}
      </button>
    );
  };

  const handleTransferRequestAction = useCallback(
    async (message, action) => {
      if (!message?.id || !action?.conversationId || !action?.requesterAttendantId) return;
      const messageId = message.id;
      const currentState = transferActionByMessageId[messageId]?.status;
      const currentRefState = transferActionStatusRef.current.get(messageId);
      if (
        currentState === 'loading' ||
        currentState === 'accepted' ||
        currentRefState === 'loading' ||
        currentRefState === 'accepted'
      ) {
        return;
      }
      transferActionStatusRef.current.set(messageId, 'loading');

      setTransferActionByMessageId((prev) => ({
        ...prev,
        [messageId]: { status: 'loading', error: '' },
      }));

      try {
        await assignConversationToAttendant(
          action.conversationId,
          action.requesterAttendantId,
          companyId,
          '',
          messageId
        );

        setMessages((prev) =>
          prev.map((item) => {
            if (item?.id !== messageId) return item;
            return {
              ...item,
              metadata: {
                ...(item.metadata || {}),
                status: 'accepted',
                responded_at: new Date().toISOString(),
              },
            };
          })
        );
        setTransferActionByMessageId((prev) => ({
          ...prev,
          [messageId]: { status: 'accepted', error: '' },
        }));
        transferActionStatusRef.current.set(messageId, 'accepted');
        showSuccess('Atendimento transferido.');
        dispatchChatStateRefreshEvent({
          reason: 'conversation_updated',
          conversation_id: action.conversationId,
        });
        if (selectedThreadId) {
          loadMessages(selectedThreadId);
        }
      } catch (err) {
        const messageText = extractApiErrorMessage(
          err,
          'Não foi possível transferir o atendimento.'
        );
        setTransferActionByMessageId((prev) => ({
          ...prev,
          [messageId]: { status: 'error', error: messageText },
        }));
        transferActionStatusRef.current.set(messageId, 'error');
        showError(messageText);
      }
    },
    [companyId, loadMessages, selectedThreadId, transferActionByMessageId]
  );

  const renderMessage = (message) => {
    const isOwn = attendantId != null && Number(message.sender_id) === attendantId;
    const timestamp = formatTimestamp(message.created_at);
    const mediaUrl = resolveMediaUrl(message.media_url);
    const fileName = message.file_name;
    const mediaKind = resolveMediaKind(message, fileName, mediaUrl);
    const messageContent = typeof message.content === 'string' ? message.content.trim() : '';
    const transferAction = resolveTransferRequestAction(message, attendantId);
    const transferActionState = transferAction
      ? transferActionByMessageId[message.id] || null
      : null;
    const transferStatus = String(
      transferActionState?.status || transferAction?.status || ''
    ).toLowerCase();
    const isTransferDone = ['accepted', 'completed', 'done', 'transferred'].includes(transferStatus);
    const isTransferLoading = transferStatus === 'loading';
    const shouldRenderContent =
      messageContent &&
      (!mediaUrl || !fileName || messageContent !== String(fileName).trim());

    return (
      <div
        key={message.id}
        className={`internal-chat-message ${isOwn ? 'is-own' : ''} ${mediaUrl ? 'internal-chat-message--has-media' : ''}`}
      >
        <div className="internal-chat-message__meta">
          <strong>{isOwn ? 'Você' : message.sender_name || `#${message.sender_id}`}</strong>
          {!isOwn && <span>{timestamp}</span>}
        </div>
        {shouldRenderContent && <p>{messageContent}</p>}
        {mediaUrl && (
          <div className={`internal-chat-message__media internal-chat-message__media--${mediaKind || 'file'}`}>
            {mediaKind === 'image' ? (
              <button
                type="button"
                className="internal-chat-media-thumb"
                onClick={() =>
                  setImageViewer({
                    url: mediaUrl,
                    alt: fileName || 'Imagem',
                    fileName: fileName || 'Imagem',
                  })
                }
                aria-label={`Visualizar ${fileName || 'imagem'} no chat`}
                title="Visualizar imagem"
              >
                <ImageIcon size={16} />
                <img
                  src={mediaUrl}
                  alt={fileName || 'imagem'}
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ) : mediaKind === 'video' ? (
              <video
                controls
                preload="metadata"
                src={mediaUrl}
                className="internal-chat-media-video"
              />
            ) : mediaKind === 'audio' ? (
              <DeferredAudioPlayer url={mediaUrl} rootRef={messageListRef} />
            ) : (
              <a href={mediaUrl} target="_blank" rel="noreferrer" className="internal-chat-media-file">
                <FileIcon size={16} />
                <span>{fileName || 'Arquivo'}</span>
              </a>
            )}
          </div>
        )}
        {transferAction && (
          <div className="internal-chat-message__actions">
            {isTransferDone ? (
              <span className="internal-chat-transfer-state">
                <CheckCircle2 size={16} aria-hidden="true" />
                Transferência concluída
              </span>
            ) : (
              <button
                type="button"
                className="internal-chat-transfer-action"
                onClick={() => handleTransferRequestAction(message, transferAction)}
                disabled={isTransferLoading}
                aria-label={`Transferir atendimento para ${transferAction.requesterName}`}
                title={`Transferir atendimento para ${transferAction.requesterName}`}
              >
                {isTransferLoading ? (
                  <LoaderCircle size={16} aria-hidden="true" className="internal-chat-spin" />
                ) : (
                  <MessageSquare size={16} aria-hidden="true" />
                )}
                <span>{isTransferLoading ? 'Transferindo...' : 'Transferir atendimento'}</span>
              </button>
            )}
            {transferActionState?.status === 'error' && transferActionState?.error && (
              <span className="internal-chat-transfer-error">
                {transferActionState.error}
              </span>
            )}
          </div>
        )}
        {isOwn && <div className="internal-chat-message__timestamp">{timestamp}</div>}
      </div>
    );
  };

  const sidebarContent = (
    <>
      <div className="internal-chat-sidebar__header">
        <div className="internal-chat-sidebar__title-row">
          <div className="internal-chat-sidebar__title">
            <span>
              {isAdmin && adminViewMode === 'all' ? 'Conversas da equipe' : 'Conversas'}
            </span>
            {scopedUnreadCount > 0 && (
              <span
                className="internal-chat-sidebar__badge"
                title={`Novas mensagens: ${scopedUnreadCount}`}
                aria-label={`Novas mensagens: ${scopedUnreadCount}`}
              >
                {scopedUnreadCount}
              </span>
            )}
          </div>
          <button
            type="button"
            className="internal-chat-sidebar__settings-btn"
            onClick={() => setIsSidebarSettingsOpen(true)}
            aria-label="Abrir configurações da tela de chat interno"
            title="Configurações"
          >
            <Settings size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
      {isSidebarSettingsOpen && (
        settingsModalHost
          ? createPortal(
            <div
              className="internal-chat-settings-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Configurações do chat interno"
              onClick={() => setIsSidebarSettingsOpen(false)}
            >
              <div
                className="internal-chat-settings-modal__surface"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="internal-chat-settings-modal__header">
                  <strong>Configurações da tela</strong>
                  <button
                    type="button"
                    className="internal-chat-settings-modal__close"
                    onClick={() => setIsSidebarSettingsOpen(false)}
                    aria-label="Fechar configurações"
                    title="Fechar"
                  >
                    <X size={16} />
                  </button>
                </div>
                {isAdmin && (
                  <div className="internal-chat-sidebar__admin-toggle" role="group" aria-label="Modo de visualização do admin">
                    <button
                      type="button"
                      className={adminViewMode === 'all' ? 'is-active' : ''}
                      onClick={handleSwitchToAdminMode}
                    >
                      Ver conversas como admin
                    </button>
                    <button
                      type="button"
                      className={adminViewMode === 'mine' ? 'is-active' : ''}
                      onClick={handleSwitchToMineMode}
                    >
                      Ver apenas minhas conversas
                    </button>
                  </div>
                )}
                {isAdmin && adminViewMode === 'all' && (
                  <div className="internal-chat-sidebar__admin-picker">
                    <div className="internal-chat-sidebar__sort internal-chat-sidebar__admin-filter">
                      <span>Atendente para monitorar</span>
                    </div>
                    <p className="internal-chat-sidebar__monitor-help">
                      Selecione um atendente abaixo para acompanhar as conversas internas dele.
                    </p>
                    <label className="internal-chat-sidebar__search" aria-label="Buscar atendente para monitorar">
                      <Search size={14} aria-hidden="true" />
                      <input
                        type="search"
                        value={adminDirectorySearchInput}
                        onChange={(event) => setAdminDirectorySearchInput(event.target.value)}
                        placeholder="Buscar atendente"
                        autoComplete="off"
                      />
                    </label>
                    {selectedAdminAttendantId ? (
                      <div className="internal-chat-sidebar__admin-selected">
                        <span>
                          Selecionado: {selectedAdminAttendantName || `Atendente #${selectedAdminAttendantId}`}
                        </span>
                        <button
                          type="button"
                          onClick={handleClearAdminAttendantSelection}
                        >
                          Limpar
                        </button>
                      </div>
                    ) : null}
                    <div className="internal-chat-sidebar__admin-results">
                      {adminDirectoryLoading ? (
                        <p className="internal-chat-empty">Carregando atendentes…</p>
                      ) : adminDirectoryError ? (
                        <p className="internal-chat-empty">{adminDirectoryError}</p>
                      ) : adminDirectoryAttendants.length === 0 ? (
                        <p className="internal-chat-empty">Nenhum atendente encontrado.</p>
                      ) : (
                        adminDirectoryAttendants.map((attendant) => {
                          const isSelected =
                            Number(attendant?.id) === Number(selectedAdminAttendantId);
                          const displayName = resolveAttendantDisplayName(attendant);
                          return (
                            <button
                              key={attendant.id}
                              type="button"
                              className={`internal-chat-sidebar__admin-result ${isSelected ? 'is-active' : ''}`}
                              onClick={() => handleSelectAdminAttendant(attendant)}
                            >
                              <span className={`presence-dot ${attendant?.is_online ? 'is-online' : 'is-offline'}`} />
                              <span>{displayName}</span>
                            </button>
                          );
                        })
                      )}
                      {!adminDirectoryLoading && adminDirectoryHasMore && !adminDirectoryError ? (
                        <button
                          type="button"
                          className="internal-chat-attendants__load-more"
                          onClick={handleLoadMoreAdminAttendants}
                        >
                          Carregar mais
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>,
            settingsModalHost
          )
          : null
      )}
      {isAdmin && adminViewMode === 'all' && (
        <div className="internal-chat-sidebar__monitor">
          <p className="internal-chat-sidebar__monitor-help">
            {selectedAdminAttendantId
              ? `Monitorando: ${selectedAdminAttendantName || `Atendente #${selectedAdminAttendantId}`}`
              : 'Modo admin: escolha um atendente para acompanhar as conversas internas dele.'}
          </p>
          <button
            type="button"
            className="internal-chat-attendants__load-more"
            onClick={() => setIsSidebarSettingsOpen(true)}
          >
            {selectedAdminAttendantId ? 'Trocar atendente' : 'Selecionar atendente'}
          </button>
        </div>
      )}
      {(!isAdmin || adminViewMode === 'mine') && (
        <label className="internal-chat-sidebar__search" aria-label="Buscar atendente">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            value={directorySearchInput}
            onChange={(event) => setDirectorySearchInput(event.target.value)}
            placeholder="Buscar atendente"
            autoComplete="off"
          />
        </label>
      )}
      <div
        ref={sidebarListRef}
        className="internal-chat-attendants"
        onScroll={handleSidebarScroll}
      >
        {threadsLoading && (
          <p className="internal-chat-empty">
            {isAdmin && adminViewMode === 'all'
              ? 'Carregando conversas do atendente…'
              : 'Carregando conversas…'}
          </p>
        )}
        {!threadsLoading && threadsError && (
          <p className="internal-chat-empty">{threadsError}</p>
        )}
        {!threadsLoading && !threadsError && directoryError && (!isAdmin || adminViewMode === 'mine') && displayedAttendants.length === 0 && (
          <p className="internal-chat-empty">{directoryError}</p>
        )}
        {!threadsLoading && !threadsError && isAdmin && adminViewMode === 'all' && visibleAdminThreads.length === 0 && (
          <p className="internal-chat-empty">
            {selectedAdminAttendantId
              ? 'Nenhuma conversa interna encontrada para este atendente.'
              : 'Clique em “Selecionar atendente” acima para escolher quem deseja monitorar.'}
          </p>
        )}
        {!threadsLoading && directoryLoading && !threadsError && (!isAdmin || adminViewMode === 'mine') && displayedAttendants.length === 0 && (
          <p className="internal-chat-empty">Carregando diretório de atendentes…</p>
        )}
        {!threadsLoading && !directoryLoading && !directoryError && !threadsError && (!isAdmin || adminViewMode === 'mine') && attendantsList.length === 0 && (
          <p className="internal-chat-empty">Nenhum outro atendente disponível.</p>
        )}
        {!threadsLoading && isAdmin && adminViewMode === 'all' && selectedAdminAttendantId && displayedAdminThreads.map(renderAdminThreadRow)}
        {!threadsLoading && (!isAdmin || adminViewMode === 'mine') && displayedAttendants.map(renderAttendantRow)}
        {!threadsLoading && hasHiddenRenderedAdminThreads ? (
          <button
            type="button"
            className="internal-chat-attendants__load-more"
            onClick={growSidebarRenderLimit}
          >
            Mostrar mais conversas
          </button>
        ) : null}
        {!threadsLoading && hasHiddenRenderedAttendants ? (
          <button
            type="button"
            className="internal-chat-attendants__load-more"
            onClick={growSidebarRenderLimit}
          >
            Mostrar mais atendentes
          </button>
        ) : null}
        {!isAdmin || adminViewMode === 'mine'
          ? (!directoryLoading && directoryHasMore && !directoryError && (
              <button
                type="button"
                className="internal-chat-attendants__load-more"
                onClick={handleLoadMoreAttendants}
              >
                Carregar mais
              </button>
            ))
          : null}
      </div>
    </>
  );

  const conversationContent = !activeThread ? (
    <div className="internal-chat-empty">
      {isAdmin && adminViewMode === 'all'
        ? !selectedAdminAttendantId
          ? 'No modo admin, primeiro clique em “Selecionar atendente” na lateral e escolha quem deseja monitorar.'
          : 'Selecione uma conversa na lateral para visualizar o histórico do atendente monitorado.'
        : 'Selecione um atendente para visualizar o histórico ou iniciar uma conversa privativa.'}
    </div>
  ) : (
    <>
      {isDraggingImage && (
        <div className="internal-chat-dropzone">
          <div className="internal-chat-dropzone__card">
            <ImageIcon size={22} />
            <strong>Solte a imagem para anexar</strong>
            <span>Ela ficará pronta para envio nesta conversa.</span>
          </div>
        </div>
      )}
      <header className="internal-chat-panel__header">
        <div className="internal-chat-panel__participant">
          {isFloating && (
            <button
              type="button"
              className="internal-chat-panel__back-btn"
              onClick={() => setCompactView('list')}
              aria-label="Voltar para a lista de conversas"
              title="Voltar para a lista de conversas"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <Avatar
            name={activeParticipantName}
            photo={activeParticipantPhoto}
            size={52}
            className="internal-chat-panel__avatar"
          />
          <div className="internal-chat-panel__details">
            <strong>{activeParticipantName}</strong>
            <span className="internal-chat-panel__status">
              <span
                className={`presence-dot ${activeStatusIsOnline ? 'is-online' : 'is-offline'}`}
              />
              {activeStatusLabel}
            </span>
            {activeThreadLastActivityAt && (
              <span className="internal-chat-panel__last-activity">
                Última atividade: {formatTimestamp(activeThreadLastActivityAt)}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="internal-chat-body" ref={messageListRef}>
        {!messagesLoading && !messagesError && messagesHasOlder && (
          <div className="internal-chat-body__load-older-wrap">
            <button
              type="button"
              className="internal-chat-body__load-older"
              onClick={handleLoadOlderMessages}
              disabled={loadingOlderMessages}
            >
              {loadingOlderMessages ? 'Carregando mensagens anteriores…' : 'Carregar mensagens anteriores'}
            </button>
          </div>
        )}
        {messagesLoading && messages.length === 0 && (
          <div className="internal-chat-empty">Carregando mensagens…</div>
        )}
        {messagesError && (
          <div className="internal-chat-empty">{messagesError}</div>
        )}
        {!messagesLoading && !messagesError && messages.length === 0 && (
          <div className="internal-chat-empty">Sem histórico ainda. Envie a primeira mensagem!</div>
        )}
        {messages.map(renderMessage)}
        <div ref={bottomRef} />
      </div>

      {activeThreadCanReply && recordedAudio && (
        <div className="internal-chat-audio-preview">
          <audio controls src={recordedAudioUrl || undefined} />
          <button
            type="button"
            className="internal-chat-audio-preview__btn"
            onClick={handleCancelRecordedAudio}
            aria-label="Cancelar áudio"
            title="Cancelar áudio"
          >
            <X size={14} />
          </button>
          <button
            type="button"
            className="internal-chat-audio-preview__btn is-send"
            onClick={handleSendRecordedAudio}
            disabled={sending}
            aria-label="Enviar áudio"
            title="Enviar áudio"
          >
            <Send size={14} />
          </button>
        </div>
      )}

      {activeThreadCanReply ? (
        <form className="internal-chat-composer" onSubmit={handleSend}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.txt,.csv"
            onChange={handleFileChange}
          />
          <div className="internal-chat-composer__actions">
            <button
              type="button"
              className="internal-chat-attach-btn"
              onClick={handleFileButtonClick}
              disabled={sending || isRecording}
              title="Anexar arquivo (imagens, vídeos, documentos)"
            >
              <Paperclip size={16} />
              Anexar
            </button>
            <button
              type="button"
              className={`internal-chat-record-btn ${isRecording ? 'is-recording' : ''}`}
              onClick={handleToggleRecording}
              disabled={sending}
              title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
              aria-label={isRecording ? 'Parar gravação' : 'Gravar áudio'}
            >
              {isRecording ? <Square size={14} /> : <Mic size={14} />}
              {isRecording ? 'Parar' : 'Áudio'}
            </button>
            {selectedFile && (
              <div className="internal-chat-selected-file">
                {selectedFilePreviewUrl && isSelectedFileImage && (
                  <img
                    src={selectedFilePreviewUrl}
                    alt={selectedFile.name || 'Prévia do arquivo'}
                    className="internal-chat-selected-file__thumb"
                  />
                )}
                {selectedFilePreviewUrl && isSelectedFileVideo && (
                  <video
                    className="internal-chat-selected-file__thumb"
                    src={selectedFilePreviewUrl}
                  />
                )}
                <span>{selectedFile.name}</span>
                {isSelectedFileAudio && (
                  <small className="internal-chat-selected-file__meta">Áudio</small>
                )}
                <button type="button" onClick={handleRemoveFile} aria-label="Remover anexo">
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
          <textarea
            ref={inputRef}
            value={inputValue}
            onPaste={handlePasteImage}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (!sending && (inputValue.trim() || selectedFile || recordedAudio)) {
                  handleSend(event);
                }
              }
            }}
            placeholder="Escreva uma mensagem..."
            rows={3}
          />
          <button type="submit" disabled={sending || (!inputValue.trim() && !selectedFile && !recordedAudio)}>
            {sending ? 'Enviando…' : (
              <>
                <Send size={16} />
                {recordedAudio ? 'Enviar áudio' : 'Enviar'}
              </>
            )}
          </button>
        </form>
      ) : (
        <div className="internal-chat-observer-note">
          Modo de visualização: esta thread pertence a outros atendentes. O admin pode acompanhar as mensagens, mas não responde por aqui.
        </div>
      )}
    </>
  );

  const imageViewerModal = imageViewer ? (
    <ImageLightbox
      src={imageViewer.url}
      alt={imageViewer.alt || 'Imagem'}
      title={imageViewer.fileName || 'Imagem'}
      downloadName={imageViewer.fileName || 'imagem'}
      onClose={() => setImageViewer(null)}
    />
  ) : null;

  if (missingAuth) {
    const accessDenied = attendantId && companyId && !canAccessInternalChat;
    return (
      <div className={isFloating ? 'internal-chat-shell internal-chat-shell--floating' : 'page internal-chat-page'}>
        <div className="internal-chat-error">
          {accessDenied ? (
            <>
              Chat interno indisponível para este perfil.<br />
              <small>Acesso não permitido.</small>
            </>
          ) : (
            <>
              Erro: Dados de autenticação ausentes.<br />
              Realize o login novamente para acessar o chat interno.<br />
              <small>attendant_id ou company_id não encontrados.</small>
            </>
          )}
        </div>
      </div>
    );
  }

  if (isFloating) {
    const showConversation = compactView === 'conversation' && activeThread;
    return (
      <div className="internal-chat-shell internal-chat-shell--floating">
        <header className="internal-chat-floating__topbar">
          <div className="internal-chat-floating__title">
            <MessageSquare size={18} aria-hidden="true" />
            <strong>{showConversation ? activeParticipantName : 'Chat interno'}</strong>
            {scopedUnreadCount > 0 && (
              <span className="internal-chat-floating__badge" aria-label={`${scopedUnreadCount} novas mensagens`}>
                {scopedUnreadCount}
              </span>
            )}
          </div>
          <div className="internal-chat-floating__actions">
            <button
              type="button"
              className="internal-chat-floating__action-btn"
              onClick={() => onMinimize?.()}
              aria-label="Minimizar mini chat"
              title="Minimizar mini chat"
            >
              <Minus size={16} />
            </button>
            <button
              type="button"
              className="internal-chat-floating__action-btn"
              onClick={() => onClose?.()}
              aria-label="Fechar mini chat"
              title="Fechar mini chat"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        {error && <div className="internal-chat-error">{error}</div>}

        <div className="internal-chat-content internal-chat-content--floating">
          {!showConversation ? (
            <aside className="internal-chat-sidebar internal-chat-sidebar--floating">
              {sidebarContent}
            </aside>
          ) : (
            <section
              className={`internal-chat-panel internal-chat-panel--floating ${isDraggingImage ? 'is-drop-active' : ''}`}
              onDragEnter={handlePanelDragEnter}
              onDragOver={handlePanelDragOver}
              onDragLeave={handlePanelDragLeave}
              onDrop={handlePanelDrop}
            >
              {conversationContent}
            </section>
          )}
        </div>
        {imageViewerModal}
      </div>
    );
  }

  const launcherPositionLabel =
    launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.NONE
      ? 'Sem atalho'
      : launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.HEADER
        ? 'Header do sistema'
        : 'Canto inferior esquerdo';

  return (
    <div className="page internal-chat-page">
      <div className="internal-chat-header page-section-header">
        <div className="page-section-header__title">
          <MessageSquare size={26} aria-hidden="true" />
          <div>
            <h1>Mensagens internas</h1>
            <p>
              {isAdmin && adminViewMode === 'all'
                ? 'Selecione um atendente para acompanhar as conversas internas dele.'
                : 'Selecione qualquer atendente ao lado para iniciar uma conversa privada em tempo real.'}
            </p>
          </div>
        </div>

        <div className="internal-chat-header__actions page-section-header__actions">
          <div className="internal-chat-shortcut" ref={shortcutMenuRef}>
            <button
              type="button"
              className={`internal-chat-shortcut__trigger ${shortcutMenuOpen ? 'is-open' : ''}`}
              onClick={() => setShortcutMenuOpen((prev) => !prev)}
              aria-haspopup="menu"
              aria-expanded={shortcutMenuOpen}
              disabled={isSavingLauncherPosition}
            >
              <span className="internal-chat-shortcut__trigger-copy">
                <strong className="is-long">Adicionar atalho</strong>
                <strong className="is-short">Atalho</strong>
                <small>
                  {isSavingLauncherPosition ? 'Salvando atalho...' : `Atual: ${launcherPositionLabel}`}
                </small>
              </span>
              <ChevronDown size={16} aria-hidden="true" />
            </button>

            {shortcutMenuOpen && (
              <div className="internal-chat-shortcut__menu" role="menu" aria-label="Adicionar atalho do mini chat">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.NONE}
                  className={`internal-chat-shortcut__option ${
                    launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.NONE
                      ? 'is-active'
                      : ''
                  }`}
                  onClick={() =>
                    handleChangeLauncherPosition(INTERNAL_CHAT_LAUNCHER_POSITIONS.NONE)
                  }
                  disabled={isSavingLauncherPosition}
                >
                  <span className="internal-chat-shortcut__option-title">Sem atalho</span>
                  <small className="internal-chat-shortcut__option-text">
                    Não exibe botão flutuante nem botão no header.
                  </small>
                </button>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.BOTTOM_LEFT}
                  className={`internal-chat-shortcut__option ${
                    launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.BOTTOM_LEFT
                      ? 'is-active'
                      : ''
                  }`}
                  onClick={() =>
                    handleChangeLauncherPosition(INTERNAL_CHAT_LAUNCHER_POSITIONS.BOTTOM_LEFT)
                  }
                  disabled={isSavingLauncherPosition}
                >
                  <span className="internal-chat-shortcut__option-title">Canto inferior esquerdo</span>
                  <small className="internal-chat-shortcut__option-text">
                    Mantém o botão flutuante e o mini chat abrindo pelo canto.
                  </small>
                </button>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.HEADER}
                  className={`internal-chat-shortcut__option ${
                    launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.HEADER
                      ? 'is-active'
                      : ''
                  }`}
                  onClick={() =>
                    handleChangeLauncherPosition(INTERNAL_CHAT_LAUNCHER_POSITIONS.HEADER)
                  }
                  disabled={isSavingLauncherPosition}
                >
                  <span className="internal-chat-shortcut__option-title">Header do sistema</span>
                  <small className="internal-chat-shortcut__option-text">
                    Coloca o atalho no header e abre o mini chat como dropdown compacto.
                  </small>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="internal-chat-error">{error}</div>}

      <div className="internal-chat-content">
        <aside className="internal-chat-sidebar">
          {sidebarContent}
        </aside>

        <section
          className={`internal-chat-panel ${isDraggingImage ? 'is-drop-active' : ''}`}
          onDragEnter={handlePanelDragEnter}
          onDragOver={handlePanelDragOver}
          onDragLeave={handlePanelDragLeave}
          onDrop={handlePanelDrop}
        >
          {conversationContent}
        </section>
      </div>
      {imageViewerModal}
    </div>
  );
}

export default function InternalChat() {
  return <InternalChatView />;
}
